'use client';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, useSyncExternalStore, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { interpolate, useTranslation } from '../../lib/i18n';
import { usePersistentState } from '../../lib/usePersistentState';
import type { SaveStatus } from '../../lib/useAutoSaveStatus';
import { useOperationFailure, useToast } from '../../components/ui/Toast';
import { holdsAnswer, releasedBy, releasing, SaveConflictError } from '../../lib/saveFeedback';
import { useReasoningLevel } from './useReasoningLevel';
import { useBrainSessions, useBrainCommands, useConfig, useMe, QUERY_KEYS } from '../../lib/queries';
import { invalidateConversationLists, renameConversation, setConversationArchive } from '../../lib/mutations/brain';
import { randomId } from 'elowen-plugin-shared/randomId';
import { ElowenApiError, WRITE_OUTCOME_UNKNOWN, apiErrorMessage, elowenClient, getConnectionPolicy, isUnconfirmedWrite, loadConnectionPolicy, typedRefusalMessage } from '../../lib/elowenClient';
import { AUTH_CLEARED_EVENT, authGeneration, subscribeAuthTransitions } from '../../lib/token';
import { subscribeConversationRemovals, type ConversationRemoval } from '../../lib/useElowenEvents';
import type { ConnectionState, RetryProgress } from 'elowen-plugin-shared/retry';
import type { AskAnswer, AskQuestion, BrainCard, BrainConversationMembership, ReactionEmoji, BrainGoal, BrainModelOption, BrainPendingPlan, BrainProject, BrainQueueMemberItem, BrainSessionSnapshot, ProcessInfo, ProjectExecutionRef, BrainStatus, BrainUsage, BrainWorkMode, McpServerStatus, SlashCommandDef, StatuslineConfig } from '../../lib/types';
import { executionRefKey } from '../../lib/types';

import { changesChatStatus } from '../../lib/chatPresentation';
import { composeAttachmentMessage } from '../../lib/chatAttachments';
import type { BrainChatRailSection, BrainRecalledInput, BrainSessionIdentity, BrainStreamControl, ChatEventView } from '../../../src/shared/wireContract';
import { collectSubagents, collectWorkflows, emptyView, fromSnapshot, liveNarration, reduce, upsertCard, type ChatTurn, type ChatView, type SubagentState, type TranscriptEvent, type WorkflowState } from '../../lib/transcript';
import { getBrainClientId, buildBinding, type BrainBinding } from '../../lib/brainSession';
import { resolveStreamSilence } from '../../lib/streamWatchdog';
import { chatModelCatalog } from '../../lib/modelCatalog';
import { isBackgroundProcessCardId } from '../../lib/processScope';
import { PluginChatPickerHost, type PluginPickerInvocation } from '../../lib/pluginChatUi';
import {
  consumePendingBrainComposer,
  consumePendingBrainSession,
  mergeBrainComposerText,
  registerBrainConversationDockOpener,
  registerBrainConversationStarter,
  type BrainOpenRequest,
} from '../../lib/brainDock';
import { uploadAttachment, type AttachRefusal, type Attachment, type PendingUpload } from './brainChatAttachments';
import { isUnseenPosition, useBrainChatHistory } from './brainChatHistory';
import { useBrainChatStream } from './brainChatStream';
import { snapshotUsage, useBrainChatUsage } from './brainChatUsage';
import { useBrainChatLifecycle } from './useBrainChatLifecycle';
import { usePromptHistory } from './usePromptHistory';
import { useConversationTyping } from './useConversationTyping';
import { createComposerDraft, type ComposerIntent } from './composerDraft';
import { mentionIdsIn } from 'elowen-plugin-shared/composerMentions';

const THOUGHTS_VALUES = ['show', 'hide'] as const;
/** The command catalog before it has loaded. Module-level so it is one identity for the app's lifetime. */
const NO_COMMANDS: SlashCommandDef[] = [];
const NO_QUEUE: BrainQueueMemberItem[] = [];

export interface BrainChatConnectionFailure {
  source: 'start' | 'policy' | 'stream';
  status: number | null;
  code: string | null;
}
const withoutBackgroundProcessCards = (cards: readonly BrainCard[]): BrainCard[] =>
  cards.filter((card) => !isBackgroundProcessCardId(card.id));

/** The `kind:'mode'` commands, in one place — the `/plan`, `/build`, `/workflow` slash names ARE the mode
 *  values, so this is what narrows a command name to a BrainWorkMode without a cast. */
const WORK_MODES: readonly BrainWorkMode[] = ['build', 'plan', 'workflow'];

type Ask = { id: string; questions: AskQuestion[]; kind?: 'approval' };
/** `fromComposer`: the command was typed or picked in the composer, so the field holds it and is consumed. */
export interface RunSlashOptions { fromComposer?: boolean }

/** A transcript on screen while the binding stays on the live conversation. `conversation` is the session
 *  of the account's own listing the view belongs to (a focused child's parent, or the viewed session), null
 *  when it is named only by `title`, the name its opener listed it under. */
interface ReadOnlyView { sessionId: string; delegated: boolean; conversation: string | null; title: string | null }
/** Exists before the HTTP response; a terminal stream result must survive a late queued acknowledgement. */
interface ModelSwitchAttempt {
  selection: BrainModelOption;
  session: string | undefined;
  /** The conversation row the switch writes: the focused child's own, else the bound conversation. A read that
   *  checks the outcome reads THIS row, never the parent when the child was written. */
  target: string | undefined;
  started: boolean;
  outcome: 'saved' | 'error' | null;
  previousEvents: Set<string>;
}

/** The non-numeric half of the daemon's status poll — everything the telemetry panel shows beside the
 *  usage numbers. A null section is one the daemon does not report (no directory, MCP off or hidden from
 *  this account, an older daemon) and the panel simply omits it. */
interface BrainTelemetry {
  projectRef?: BrainStatus['projectRef'] | null;
  project: BrainProject | null;
  chatRailSections: readonly BrainChatRailSection[];
  mcp: McpServerStatus[] | null;
}
const EMPTY_TELEMETRY: BrainTelemetry = { project: null, chatRailSections: [], mcp: null };

/** The membership half of a snapshot or status read, without the model identity it travels with. */
const membershipOf = ({ owner, ownerId, participants, isOwner, agentPaused, capabilities, notifications }: BrainConversationMembership): BrainConversationMembership =>
  ({ owner, ownerId, participants, isOwner, agentPaused, capabilities, notifications });

/** A send refused because one mentioned account left the conversation names that account; only its
 *  mention is dropped when the text goes back into the composer. */
const staleMentionSchema = z.object({ error: z.literal('mention_not_participant'), userId: z.number().int().positive() });
function staleMention(error: unknown): number | undefined {
  if (!(error instanceof ElowenApiError) || error.code !== 'mention_not_participant') return undefined;
  return staleMentionSchema.parse(error.details).userId;
}


/** Project and instance telemetry retain their existing field-specific hydration fences. */
const telemetryOf = (st: BrainStatus): Omit<BrainTelemetry, 'chatRailSections'> => ({
  projectRef: st.projectRef ?? null,
  project: st.project ?? null,
  mcp: st.mcp ?? null,
});

/** The chat controller's state, split into THREE context values by how often each half changes.
 *
 *  One object for all of it is what made a streamed token the most expensive event in the app: every
 *  delta rebuilt the single value, so the shell, the top bar, the model picker, the composer, the
 *  telemetry rail and the deck hero all re-rendered for a token none of them display. The split is by
 *  UPDATE FREQUENCY, not by feature:
 *
 *    - `BrainChatActions` — the session's identity, the controls, and everything a user action changes
 *      (model catalog, modal flags, work mode). Its identity survives a whole streamed turn.
 *    - `BrainChatStatus` — what daemon EVENTS change: usage, cards, queue, question, telemetry, goal,
 *      attachments, the sub-agent and workflow projections. Several times a turn, never per token.
 *    - `BrainChatTranscript` — the per-token half: the turns and the live narration.
 *
 *  Each value keeps its identity while its own members do (`useShallowStable`), so a consumer re-renders
 *  exactly when the data it reads changes. Owned by BrainChatProvider so a Chat↔Terminál toggle or a route
 *  change (which only unmount the presentational surface) never tears down the SSE stream or draft. */
export interface BrainChatTranscript {
  turns: ChatTurn[];
  messageWindow: import('../../../src/shared/wireContract').BrainMessagePage | null;
  conversationAppendCount?: number;
  thinking: boolean;
  /** The assistant prose a reader can see right now (see `liveNarration`), handed to plugin chat dock
   *  views so a surface drawn OVER the transcript can still show what is being said. Empty when the
   *  newest turn is not the assistant speaking. */
  narration: string;
}

/** What daemon events change several times a turn. Read it only where the value is actually displayed. */
export interface BrainChatStatus {
  busy: boolean;
  /** Outstanding conversation work, including the gaps between delivery turns. */
  workPending: boolean;
  /** The latest fresh send's display identity, retained through settle so its header never remounts. */
  submittedRun: { startedAt: string; awaitingEcho: boolean; userId?: string } | null;
  ready: boolean;
  /** Recovery is scheduled or attempting after the first attachment attempt. Initial loading remains
   *  unready without being a reconnect; connectionState carries the canonical transport presentation. */
  reconnecting: boolean;
  connectionState: ConnectionState | null;
  /** The transport is recovering on its first attempt; the chat stays quiet until that attempt fails. */
  connectionFirstAttempt: boolean;
  connectionFailure: BrainChatConnectionFailure | null;
  modelRetry: RetryProgress | null;
  executionState: BrainStreamControl['executionState'];
  programStatus: BrainStreamControl['programStatus'] | null;
  /** Whether at least one chat surface is currently mounted. */
  hasSurface: boolean;
  notice: string;
  ask: Ask | null;
  cards: BrainCard[];
  processes: ProcessInfo[];
  /** The waiting queue, each entry marked with whether THIS viewer may remove it: their own, or any for
   *  the owner. Derived here once; every remove control (chip, keyboard, composer recall) reads it. */
  queued: BrainQueueMemberItem[];
  /** The bound conversation's owner and guests from its snapshot and status reads; null until one lands
   *  and while a read-only view is on screen. */
  membership: BrainConversationMembership | null;
  /** The signed-in account, or null while its read is pending. */
  viewerId: number | null;
  /** The daemon reported the viewer as a guest: owner-only controls render disabled. */
  guest: boolean;
  /** Stop may abort the running turn: always for the owner, for a guest only the turn they wrote. */
  canStop: boolean;
  turnWriter: BrainSessionSnapshot['turnWriter'] | null;
  /** Effective conversation YOLO from the existing status read; null until confirmed. */
  yolo: boolean | null;
  reissueTarget: import('../../../src/shared/wireContract').BrainReissueTarget | null;
  conversationReadState: import('../../../src/shared/wireContract').BrainConversationReadState | null;
  unreadBoundaryId: string | null;
  typing: import('../../../src/shared/wireContract').BrainTypingParticipant[];
  replyTarget: { sessionId: string; messageId: string } | null;
  recalledInputs: readonly BrainRecalledInput[];
  readOnly: string | null;
  /** Display kind from the drill-in request, never inferred as authority from a session id. */
  readOnlyChild: boolean;
  /** The title of the conversation a read-only view belongs to, as the conversation bar names it: for a
   *  delegated child the conversation it runs in, otherwise the viewed conversation. Read live from the
   *  account's own listing when it holds that conversation, else the name the opener listed it under
   *  (`BrainOpenRequest.title`). Null while no read-only view is open or no name is known. */
  readOnlyTitle: string | null;
  childDelegation: BrainSessionIdentity['delegation'];
  /** The owned delegated child currently viewed read-only; retained for targeted stop controls. */
  childFocus: string | null;
  /** The viewed delegated child's own name (Delegate's `name`, or the task-derived fallback the rail already
   *  shows), captured at the moment it was drilled into — the SAME label `subagents` rail renders for
   *  that row, or the register's `BrainOpenRequest.name`. Display only, so it is kept whether or not the
   *  drill-in owns targeted controls (`childFocus`), e.g. from a `/chat?session=` preview. Null while unknown
   *  (opened from a route that did not carry one). */
  childFocusName: string | null;
  usage: BrainUsage | null;
  /** Project, plugin rail sections and MCP from the daemon's status poll. */
  telemetry: BrainTelemetry;
  /** The conversation's autonomous goal, or null when none runs. Server-authoritative: the live `goal`
   *  event and the reconnect snapshot both replace it wholesale, so it can be cleared, never only set. */
  goal: BrainGoal | null;
  promptSuggestion: string | null;
  /** The delegated sub-agents of this transcript (latest state per child session) — the agents drill-in
   *  and the telemetry rail read this ONE projection instead of each folding the turns again. Its identity
   *  is held across a token whose fold changes nothing (`useStableList`), so a rail that lists running
   *  agents is not re-rendered by prose arriving under one of them. */
  subagents: SubagentState[];
  /** The workflows (DAGs) of this transcript, latest state per workflow id. Identity-stable, like `subagents`. */
  workflows: WorkflowState[];
  /** The statusline plugin's row config from the daemon's status: `null` when the plugin is disabled,
   *  `undefined` until the first status read lands (see `useModelControlHost`). */
  lineCfg: StatuslineConfig | null | undefined;
  /** Whether the conversation's current model runs on its Fast route (the status row's `fast`). */
  fast: boolean;
  attachments: Attachment[];
  /** Files still on their way up, with the bytes the server has accepted so far. */
  uploads: PendingUpload[];
  /** Whether an older page of stored history remains to lazy-load (drives the scroll-up sentinel). */
  hasMoreHistory: boolean;
  /** Bumped when the composer should take focus (compose bridge / seeded draft); the surface watches it. */
  focusNonce: number;
  /** Bumped by every send this window makes; a surface watches it to bring its transcript back to the newest turn. */
  sendNonce: number;
  /** The plan waiting on the user's implement/cancel decision, `null` when none is. Derived from the
   *  DAEMON's answer (mode + submitted plan, hydrated on connect and on every snapshot frame) and kept in
   *  step by the transcript, so the decision survives a reload, a second tab, and plan mode having been
   *  entered from another surface — none of which tab-local state could ever see. */
  planDecision: BrainPendingPlan | null;
  /** True while an approval is in flight — the button disables itself instead of firing twice. */
  planSubmitting: boolean;
}

/** The session's identity and every control. Stable across a whole streamed turn: the callbacks keep one
 *  identity for the provider's lifetime (`useStableActions`), so only a real user action invalidates it. */
export interface BrainChatActions {
  /** Called by a chat surface on mount; cleanup unregisters its rendered-answer visibility reader.
   *  Activity is acknowledged only when a surface actually shows the latest answer. */
  registerSurface: (latestVisible: () => boolean) => () => void;
  /** Wake bounded activity acknowledgement when the latest rendered answer becomes visible. */
  notifyActivityVisibility: () => void;
  statsOpen: boolean;
  setStatsOpen: (v: boolean) => void;
  /** `/reasoning` and the open state of the bar's reasoning popover — the effort picker plus the Thought-rows
   *  switch (the CLI's `/reasoning show`). */
  reasoningOpen: boolean;
  setReasoningOpen: (v: boolean) => void;
  /** Plugin-owned picker opened from a surface-local slash command. */
  pluginPicker: PluginPickerInvocation | null;
  /** The open picker threw while rendering. The surface says so in place; the picker host draws nothing. */
  pluginPickerCrashed: boolean;
  /** The picker host reports that this invocation's picker threw while rendering. */
  crashPluginPicker: (invocation: PluginPickerInvocation) => void;
  closePluginPicker: () => void;
  sendPluginPicker: (text: string | import('../../../src/shared/wireContract').CommandInvocation) => void;
  /** One help surface, opened on the requested catalog. */
  helpTab: 'commands' | 'shortcuts' | null;
  openHelp: (tab?: 'commands' | 'shortcuts') => void;
  closeHelp: () => void;
  /** `/model` and the open state of the model picker menu. The surface mounts exactly one ModelPicker (the
   *  statusline's, or the bar's, which a phone folds into the overflow menu), and its dropdown is controlled
   *  by this flag, so the slash opens the visible picker instead of a second surface. */
  modelOpen: boolean;
  setModelOpen: (v: boolean) => void;
  activeSessionId: string | null;
  /** Attempt an explicit dock close; false keeps the current surface visible. */
  closeDock: () => Promise<boolean>;
  /** Saved icon of the viewed stream identity, including read-only conversations outside the owned list. */
  conversationIcon: BrainSessionIdentity['icon'];
  /** The conversation the plugin chat surfaces (cards, rail sections, pickers) may act on through their
   *  own routes: the viewer's own conversation, or null while a delegated child or a read-only transcript
   *  is on screen. Those are drill-in views, and a plugin route writes only into the caller's own
   *  conversations (`BrainService.writePluginCard`). What the surfaces SHOW never depends on it: they
   *  draw the session's cards from the stream, the same snapshot and events the CLI draws. */
  pluginSessionId: string | null;
  draft: ReturnType<typeof createComposerDraft>;
  conversationName: string;
  setConversationName: (name: string) => void;
  setInput: React.Dispatch<React.SetStateAction<string>>;
  acceptPromptSuggestion: () => void;
  addFiles: (files: Iterable<File>) => Promise<void>;
  removeAttachment: (index: number) => void;
  /** Stop an upload that is still running (or still waiting its turn) and discard what was sent. */
  cancelUpload: (key: number) => void;
  submit: (delivery?: import('../../../src/shared/wireContract').BrainInputDelivery) => Promise<void>;
  onQueueSteer: (id: string) => Promise<void>;
  reissue: (target: import('../../../src/shared/wireContract').BrainReissueTarget, replacementText?: string) => Promise<void>;
  revokeConversationApprovals: () => Promise<void>;
  beginReply: (messageId: string) => void;
  cancelReply: () => void;
  restoreRecalledInput: (id: string) => void;
  reportTyping: (hasText: boolean) => void;
  openMessageWindow: (messageId: string) => Promise<void>;
  closeMessageWindow: () => void;
  switchSession: (opts: { session?: string; fresh?: boolean }) => Promise<void>;
  /** Create a fresh conversation. Start authorizes `target` before the first spawn and instruction read;
   *  a `message` is sent once the conversation runs there. Without a target the
   *  daemon's default stays (the first project of the account's order). Project menus pass their chosen
   *  target; plugins pass an unsent `draft` instead of a message. Resolves the confirmed session id.
   *  `onCreated` runs once the daemon confirmed the new conversation, before the message is sent: a caller whose
   *  send then failed knows the conversation exists. `resume` names that conversation, and the message is sent into
   *  it instead of creating another one. */
  startNewConversation(opts?: { message?: string; draft?: string; target?: ProjectExecutionRef; resume?: string; onCreated?: (session: string) => void }): Promise<string>;
  /** THE Project move of this conversation, owned here so every picker (the bar's wide and overflow placements,
   *  a remounted one) shares one in-flight state: choosing what the conversation already runs in writes nothing,
   *  a move that is running or whose answer was lost is neither stacked on nor repeated, and every failure is
   *  reported here once (a lost answer is held until the daemon's identity is read back). Resolves the
   *  daemon's response for the caller to follow its environment operation, and undefined when nothing was
   *  committed. */
  selectProjectExecution: (target: NonNullable<BrainStatus['projectRef']>, session: string) => Promise<Awaited<ReturnType<typeof elowenClient.brainSetExecution>> | undefined>;
  /** The state of that move for the open conversation, for the pickers to present. `retryTarget` is set after a
   *  failure that was not a lost answer. */
  projectMove: { status: SaveStatus; retryTarget: ProjectExecutionRef | null };
  /** Re-reads the daemon's execution identity of `session` and adopts it: the recovery of a selection whose answer
   *  was lost. Rejects when the read fails. */
  rereadProjectExecution: (session: string) => Promise<void>;
  /** Open one delegated child read-only. Owned descendants retain their targeted controls. */
  focusSubagentSession: (sessionId: string, name?: string | null) => Promise<void>;
  exitReadOnly: () => void;
  /** Delete a conversation and re-target the chat when it was the open one. Rejects when the delete failed; the caller (a confirmation) reports it. */
  deleteSession: (id: string) => Promise<void>;
  onQueueRemove: (id: string) => void;
  onQueueRecall: (all?: boolean) => void;
  onAnswer: (id: string, answers: AskAnswer[]) => Promise<void>;
  /** Re-reads the daemon's control state, so whether the parked question remains is known again after an answer
   *  whose outcome was lost. Rejects when the read fails. */
  reloadAsk: () => Promise<void>;
  /** THE reasoning writer of this conversation: the chat bar, the keyboard cycle and the model picker all write
   *  through it, so a write whose answer was lost holds every one of them until the daemon's level is read back. */
  reasoning: ReturnType<typeof useReasoningLevel>;
  /** Explicit Stop intent aborts the streaming turn for ALL watchers of the bound conversation. */
  abort: () => void;
  rewind: () => void;
  /** Lazy first attachment; the start mutation runs once, independent of GET stream recovery. */
  ensureAttached: () => void;
  /** Open a `/chat?session=` link (an alert, a shared URL), attaching on first use. The daemon decides who
   *  writes: a member's explicit start binds the conversation writable; a start it refuses (not a member,
   *  e.g. administrator oversight) opens it read-only, and one the account may not read either is
   *  unavailable and leaves the account in its own conversation. Nothing is written past a refusal. */
  openConversationLink: (sessionId: string) => void;
  retryConnection: () => void;
  /** Lazy-load older history: fetches the next backwards page, waits for the surface's `ready` gate (a
   *  touch gesture and its momentum ending) and prepends it. Resolves immediately when nothing older
   *  remains, returns the request already in flight when there is one, and rejects when the page read
   *  fails. The surface's prefetch band calls it before the reader reaches the oldest loaded turn. */
  loadOlder: (ready: () => Promise<void>) => Promise<void>;
  /** Store one of the viewer's reaction changes on a message of `session`. Rejects without a request when
   *  `session` is no longer the conversation bound on screen (a read-only or child view, or another
   *  conversation), so a click from a view that has gone cannot land elsewhere; the transcript's optimistic
   *  overlay then reverts. The live `reaction` event folds the confirmed change. */
  reactToMessage: (session: string, messageId: string, emoji: ReactionEmoji, add: boolean) => Promise<unknown>;
  /** The lazily-fetched model catalog (null until first load) — shared by the header ModelPicker and the
   *  composer `/model` slash. `[]` means the RBAC filter stripped every model for this user. */
  models: BrainModelOption[] | null;
  /** The active conversation's model id (from status / a switch) — the picker's trigger label + active mark. */
  currentModel: string;
  /** The active conversation's PUBLIC provider identity — the operator's config entry id. It is what a
   *  model pick is matched against, and what a display falls back to when no label is known. */
  provider: string;
  /** The operator's display name for that provider (`Ollama`). Everything a human reads renders
   *  `providerLabel || provider`; PI's internal `elowen-<id>` registry name reaches neither. */
  providerLabel: string;
  /** The INTERNAL pi provider of the active model — the key into the subscription-usage map, never a
   *  display value. Kept apart from `provider` because pi provider ids and config entry ids are different
   *  namespaces, and one field carrying both is what leaked `elowen-<id>` into the chat header. */
  usageProvider: string;
  /** Switch this conversation to `m` in place (respawn under the same id; no SSE reconnect). */
  setModel: (m: BrainModelOption) => Promise<boolean>;
  /** Fetch the catalog on first picker open (idempotent-cheap; re-invoked by the picker's error retry). */
  loadModels: () => void;
  modelsLoading: boolean;
  modelsError: boolean;
  /** Status for the current model switch; kept in the provider so it survives picker dismissal. */
  modelStatus: SaveStatus;
  modelQueued: boolean;
  retryModel: () => void;
  /** Whether the transcript renders the model's reasoning segments. A CLIENT-SIDE display switch only:
   *  the daemon keeps streaming `reasoning` either way, so hidden thoughts stay in the transcript and
   *  reappear the moment it is switched back on. Persisted per browser. */
  showThoughts: boolean;
  setShowThoughts: (v: boolean) => void;
  /** Render-only conversation selection from daemon control. Unknown until hydrated; sends carry no mode. */
  workMode: BrainWorkMode | undefined;
  modeSwitching: boolean;
  switchWorkMode: (mode: BrainWorkMode) => void;
  /** Approve the submitted plan: switch to build mode and send the CLI's implement prompt. Without it a web
   *  user who entered plan mode would have no way to act on the plan they just got. */
  implementPlan: () => void;
  /** Decline the decision without leaving plan mode (the CLI picker's Cancel): the plan stays in the
   *  transcript and another message keeps refining it. */
  dismissPlan: () => void;
  /** Whether the conversation bar's title editor is open: its pencil and `/rename` open it, and the
   *  controller owns the write (`renameSession`, or the draft `conversationName` before the first send). */
  renameOpen: boolean;
  openRename: () => void;
  closeRename: () => void;
  /** THE conversation switcher's open state. Every entry point — the /chat header, the dock's
   *  conversation name — opens the one modal the shell mounts beside this provider: two surfaces share
   *  this controller, and a per-surface switcher would be two lists of the same conversations. The
   *  modal itself is not rendered here, because it reads this controller in turn. */
  historyOpen: boolean;
  openHistory: () => void;
  closeHistory: () => void;
  /** Rename the bound conversation and refresh the conversation list. */
  renameSession: (title: string) => Promise<void>;
  /** The surface-filtered command catalog (`GET /brain/commands`) — the single source every menu reads. */
  commands: SlashCommandDef[];
  /** Execute a catalog command exactly as the composer's slash menu does. Exposed so a second entry point
   *  (the telemetry mascot's command field) dispatches through THIS path instead of a parallel copy. */
  runSlash: (cmd: SlashCommandDef, argument?: string, options?: RunSlashOptions) => void;
  /** The caller's conversations, narrowed to what a consumer reads. A React Query result object is a fresh
   *  identity on every render, so passing it through whole would invalidate this value per token. */
  sessions: { data: ReturnType<typeof useBrainSessions>['data']; isLoading: boolean; error: ReturnType<typeof useBrainSessions>['error']; refetch: ReturnType<typeof useBrainSessions>['refetch'] };
}

/** The whole controller, for the controller itself and for tests that drive one surface end to end. */
export type BrainChatValue = BrainChatActions & BrainChatStatus & BrainChatTranscript;

/** What the controller hands the provider: one value per update frequency. */
interface BrainChatSlices {
  actions: BrainChatActions;
  status: BrainChatStatus;
  transcript: BrainChatTranscript;
}

const BrainChatActionsContext = createContext<BrainChatActions | null>(null);
const BrainChatStatusContext = createContext<BrainChatStatus | null>(null);
const BrainChatTranscriptContext = createContext<BrainChatTranscript | null>(null);

function readChatContext<T>(value: T | null, hook: string): T {
  if (!value) throw new Error(`${hook} must be used within <BrainChatProvider>`);
  return value;
}

/** The session's identity and its controls. The default read: a consumer that only acts on the
 *  conversation is not re-rendered by anything the conversation says. */
export function useBrainChat(): BrainChatActions {
  return readChatContext(useContext(BrainChatActionsContext), 'useBrainChat');
}

/** What daemon events change (usage, cards, queue, question, telemetry, agents). Read it only where one of
 *  those values is actually displayed — it invalidates several times a turn. */
export function useBrainChatStatus(): BrainChatStatus {
  return readChatContext(useContext(BrainChatStatusContext), 'useBrainChatStatus');
}

/** The transcript itself. Invalidates on EVERY streamed token, so only the component that renders the
 *  turns (or the live narration) may read it. */
export function useBrainChatTranscript(): BrainChatTranscript {
  return readChatContext(useContext(BrainChatTranscriptContext), 'useBrainChatTranscript');
}

/** Subscribe only where the draft is displayed, never in the transcript or telemetry consumers. */
export function useBrainChatInput(): string {
  const { draft } = useBrainChat();
  return useSyncExternalStore(draft.subscribe, draft.getSnapshot, draft.getServerSnapshot);
}

/** Keep the previous object while every member is identical. The three context values are built from state
 *  that already holds its identity (React state, memoized projections, `useStableActions` callbacks), so a
 *  shallow compare is exact here: the value changes when — and only when — a member does. A missed
 *  `useMemo` dependency cannot silently widen a re-render, which is what a hand-written dependency list of
 *  forty members would eventually do. */
function useShallowStable<T extends object>(next: T): T {
  const previous = useRef(next);
  const current = previous.current;
  const keys = Object.keys(next) as (keyof T)[];
  const same = keys.length === Object.keys(current).length
    && keys.every((key) => Object.is(current[key], next[key]));
  if (!same) previous.current = next;
  return previous.current;
}

/** One identity per action, for the provider's whole lifetime, without asking every handler in the
 *  controller to be a `useCallback` with a hand-maintained dependency list (which is how a send or an abort
 *  ends up holding a stale session). Each wrapper reads the CURRENT render's implementation through a ref,
 *  so the behaviour is always the newest one and the identity never changes.
 *
 *  The action set is fixed at first render — it is a literal in the controller's return, so there is no
 *  such thing as a key appearing later. */
function useStableActions<T extends Record<string, (...args: never[]) => unknown>>(impl: T): T {
  const latest = useRef(impl);
  latest.current = impl;
  const [stable] = useState(() => Object.fromEntries(
    Object.keys(impl).map((key) => [key, (...args: unknown[]) => (latest.current[key] as (...a: unknown[]) => unknown)(...args)]),
  ) as unknown as T);
  return stable;
}

/** Hold a projection's identity while its members are unchanged. `collectSubagents`/`collectWorkflows` fold
 *  the whole transcript on every token and hand back the SAME state objects (the fold reuses the segments it
 *  did not touch), so an element-wise compare turns "the same agents as a moment ago" into the same array —
 *  and the telemetry rail stops re-rendering for prose arriving under a tool call. */
function useStableList<T>(next: readonly T[]): T[] {
  const previous = useRef(next as T[]);
  const current = previous.current;
  if (current !== next && (current.length !== next.length || !next.every((item, i) => Object.is(current[i], item)))) {
    previous.current = next as T[];
  }
  return previous.current;
}

/** The controller: owns the whole network + transcript lifecycle for the tab's single chat. Mirrors the
 *  CLI's session binding (src/cli/chat/brainClient.ts) — a stable per-tab clientId, a monotonic generation
 *  bumped on initial attachment / explicit switch, the bound session threaded through every scoped call, and
 *  stale-generation discard on late responses so a superseded A/B switch can't clobber the live view. */
function useBrainChatController(): BrainChatSlices {
  const { t } = useTranslation();
  const { toast, dismissKey } = useToast();
  const reportFailure = useOperationFailure();
  const qc = useQueryClient();
  const sessions = useBrainSessions();
  // One frozen empty catalog rather than `= []`: a fresh literal per render would change the actions value
  // on every token while the catalog is still loading.
  const commands = useBrainCommands().data ?? NO_COMMANDS;
  const { data: config } = useConfig();
  const viewerId = useMe().data?.user?.id ?? null;

  // Transcript content and current control have separate authorities: the fold renders content while
  // the daemon projection owns Stop, native program status and background liveness.
  const [view, setView] = useState<ChatView>(emptyView());
  const [conversationReadState, setConversationReadState] = useState<import('../../../src/shared/wireContract').BrainConversationReadState | null>(null);
  // The acknowledged server cursor is the sole unread owner, including cross-tab updates.
  const unreadBoundaryId = conversationReadState?.firstUnreadMessageId ?? null;
  const [typing, setTyping] = useState<import('../../../src/shared/wireContract').BrainTypingParticipant[]>([]);
  const [yolo, setYolo] = useState<boolean | null>(null);
  const yoloRead = useRef(0);
  const [reissueTarget, setReissueTarget] = useState<import('../../../src/shared/wireContract').BrainReissueTarget | null>(null);
  const [messageWindow, setMessageWindow] = useState<import('../../../src/shared/wireContract').BrainMessagePage | null>(null);
  const windowRequest = useRef(0);
  const turns = view.turns;
  // The history window decides from the rendered view whether a reconnect snapshot keeps the older pages.
  const renderedViewRef = useRef(view);
  renderedViewRef.current = view;
  const [control, setControl] = useState<BrainStreamControl | null>(null);
  const busy = control?.streaming === true;
  const workPending = control?.workPending === true;
  const [submittedRun, setSubmittedRun] = useState<BrainChatStatus['submittedRun']>(null);
  // Read by the stream handlers, which keep the closure of the render that opened the stream.
  const agentPausedRef = useRef(false);
  const writerRead = useRef(0);
  const transcriptEventRevision = useRef(0);
  const settlementDirty = useRef(false);
  const applyEvent = (e: TranscriptEvent): void => {
    if (e.type === 'typing') {
      if (e.sessionId === boundSessionRef.current && e.sessionId === activeSessionRef.current) {
        hydrationStampRef.current.typing += 1;
        setTyping(e.participants);
      }
      return;
    }
    if (e.type === 'control') { applyControl(e.control); return; }
    if (e.type === 'resync' || e.type === 'compacted') {
      transcriptEventRevision.current += 1;
      setReissueTarget(null);
    }
    if (e.type === 'user' || e.type === 'message-stored' || e.type === 'text' || e.type === 'reasoning'
      || e.type === 'tool' || e.type === 'tool_end' || e.type === 'session' || e.type === 'discard_user') {
      // Native completion and durable activity each publish idle for the same transcript head. Neither
      // supersedes its settlement read; only new transcript content or a replacement snapshot does.
      transcriptEventRevision.current += 1;
      setReissueTarget(null);
      if (e.type !== 'session') settlementDirty.current = true;
    }
    // While the agent is paused no turn starts (docs/modules/brain/turns.md): the daemon stores every send as
    // an authored message and echoes it. That echo opens no run, so it names no turn writer, does not start
    // thinking, and releases a header a send made before the pause reached this tab. A message that mentions
    // the agent is the exception: it runs its own turn, so its echo opens that run like any send.
    if (e.type === 'user' && agentPausedRef.current && !e.mentions?.some((mention) => mention.id === 'agent')) {
      setSubmittedRun(current => current?.awaitingEcho ? null : current);
      setView((cur) => ({ ...reduce(cur, e), thinking: cur.thinking }));
      return;
    }
    if (e.type === 'retry') {
      hydrationStampRef.current.control += 1;
      setModelRetry(e.retry);
      return;
    }
    if (e.type === 'idle') setControl(current => current && { ...current, streaming: false });
    // Every turn has one writer, announced by its user echo; a 1:1 echo carries no author, which is the owner.
    if (e.type === 'user') {
      const session = boundSessionRef.current;
      const generation = genRef.current;
      const read = ++writerRead.current;
      if (session) void elowenClient.brainStatus(session).then(status => {
        if (read === writerRead.current && generation === genRef.current && session === boundSessionRef.current) setTurnWriter(status.turnWriter ?? null);
      }).catch(error => {
        if (read === writerRead.current && generation === genRef.current && session === boundSessionRef.current) toast(typedRefusalMessage(error) ?? t.brainChat.statusReadError, 'error');
      });
    } else if (e.type === 'idle' || e.type === 'error') {
      writerRead.current += 1;
      setTurnWriter(null);
    }
    if (e.type === 'user') {
      setSubmittedRun(current => current?.awaitingEcho
        ? { ...current, awaitingEcho: false, userId: e.durableId } : current);
    } else if (e.type === 'message-stored' && e.durableId) {
      // The sent turn keeps its run once the echo takes its stored id.
      setSubmittedRun(current => current && current.userId === e.durableId ? { ...current, userId: e.id } : current);
    } else if (e.type === 'idle' || e.type === 'discard_user' || e.type === 'error') {
      setSubmittedRun(current => current?.awaitingEcho || (e.type === 'discard_user' && current?.userId === e.durableId)
        ? null : current);
    }
    setView((cur) => reduce(cur, e));
  };
  const activeSessionRef = useRef<string | undefined>(undefined);
  const {
    hasMoreHistory,
    loadHistory,
    refreshSettlement,
    loadOlder,
    replaceNewestWindow,
    clearWindow: clearHistoryWindow,
  } = useBrainChatHistory({
    getGeneration: () => genRef.current,
    getSession: () => activeSessionRef.current ?? boundSessionRef.current,
    getView: () => renderedViewRef.current,
    setView,
  });
  const [nameDraft, setNameDraft] = useState<{ session: string | null; name: string }>({ session: null, name: '' });
  const promptHistory = usePromptHistory();
  const [draft] = useState(createComposerDraft);
  const { setInput } = draft;
  const [ready, setReady] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  const [connectionState, setConnectionState] = useState<ConnectionState | null>(null);
  const [connectionFirstAttempt, setConnectionFirstAttempt] = useState(false);
  const [connectionFailure, setConnectionFailure] = useState<BrainChatConnectionFailure | null>(null);
  const [modelRetry, setModelRetry] = useState<RetryProgress | null>(null);
  const connectionFailed = (source: BrainChatConnectionFailure['source'], error: unknown): void => {
    setReady(false);
    setReconnecting(false);
    setConnectionFailure({ source, status: error instanceof ElowenApiError ? error.status : null,
      code: error instanceof ElowenApiError ? error.code ?? null : null });
  };
  // The dock and /chat can both mount a surface. Keep activity visibility armed until the last one leaves.
  const [surfaces, setSurfaces] = useState(0);
  const hasSurface = surfaces > 0;
  const surfaceReaders = useRef(new Set<() => boolean>());
  const registerSurface = useCallback((latestVisible: () => boolean) => {
    surfaceReaders.current.add(latestVisible);
    setSurfaces((n) => n + 1);
    return () => { surfaceReaders.current.delete(latestVisible); setSurfaces((n) => n - 1); };
  }, []);
  const [telemetry, setTelemetry] = useState<BrainTelemetry>(EMPTY_TELEMETRY);
  const [goal, setGoal] = useState<BrainGoal | null>(null);
  const [promptSuggestion, setPromptSuggestion] = useState<string | null>(null);
  const [lineCfg, setLineCfg] = useState<StatuslineConfig | null | undefined>(undefined);
  const [notice, setNotice] = useState('');
  const [ask, setAsk] = useState<Ask | null>(null);
  const [cards, setCards] = useState<BrainCard[]>([]);
  const [processes, setProcesses] = useState<ProcessInfo[]>([]);
  const [statsOpen, setStatsOpen] = useState(false);
  const [reasoningOpen, setReasoningOpen] = useState(false);
  const [pluginPicker, setPluginPicker] = useState<PluginPickerInvocation | null>(null);
  // The invocation whose picker threw. Keyed by the invocation itself, so opening another picker starts clean.
  const [crashedPicker, setCrashedPicker] = useState<PluginPickerInvocation | null>(null);
  const [helpTab, setHelpTab] = useState<'commands' | 'shortcuts' | null>(null);
  const openHelp = (tab: 'commands' | 'shortcuts' = 'commands') => setHelpTab(tab);
  const closeHelp = () => setHelpTab(null);
  const [modelOpen, setModelOpen] = useState(false);
  const [queued, setQueued] = useState<BrainQueueMemberItem[]>([]);
  // Who may change what in the bound conversation: the stream snapshot's session block at bind, then the
  // conversation list's row as members come and go; cleared on every rebind, like the Project. The daemon
  // still enforces each refusal itself.
  const [membership, setMembership] = useState<BrainConversationMembership | null>(null);
  const [turnWriter, setTurnWriter] = useState<BrainSessionSnapshot['turnWriter'] | null>(null);
  // Ids whose DELETE is in flight. The optimistic remove HIDES the item instead of taking it out of the
  // list, so a failure only has to unhide it and there is no position left to reconstruct — reconstructing
  // one from an index captured at click time is what reordered the queue whenever two failing removes
  // overlapped, and this order is the order the agent is fed in. Every server-authoritative snapshot clears
  // the set: it is newer truth, and queue ids are POSITIONAL, so a stale id would hide a different message.
  const [removingQueue, setRemovingQueue] = useState<ReadonlySet<string>>(() => new Set());
  const [readOnlyView, setReadOnlyView] = useState<ReadOnlyView | null>(null);
  const readOnly = readOnlyView?.sessionId ?? null;
  const readOnlyChild = readOnlyView?.delegated ?? false;
  const [childDelegation, setChildDelegation] = useState<BrainSessionIdentity['delegation']>();
  /** Owned child focus retains targeted controls, but all child transcripts hide the composer. */
  const [childFocus, setChildFocus] = useState<string | null>(null);
  /** The viewed child's OWN name, as the ancestor rail (`subagents`) already labels it —
   *  the same "delegation short label" the CLI rail and `SubagentState.name` fallback derive. Kept beside
   *  `childFocus` rather than merged into it: every existing caller reads `childFocus` as the plain session
   *  id, and the name follows the drill-in even when it owns no controls (`childFocus` null). */
  const [childFocusName, setChildFocusName] = useState<string | null>(null);
  /** A drilled-in child or a read-only preview is on screen while the binding stays on the parent. */
  const viewingOtherSession = readOnly !== null || childFocus !== null;
  const [activeSessionId, setActiveSessionIdState] = useState<string | null>(null);
  const setActiveSessionId = (id: string): void => { activeSessionRef.current = id; setActiveSessionIdState(id); };
  // Invites, removals and leaves publish no stream event; they reach this tab through the conversation
  // list, which the conversations feed re-reads. Its row of the bound conversation is the same projection.
  const listedMembership = sessions.data?.find((session) => session.id === activeSessionId);
  useEffect(() => {
    if (listedMembership && listedMembership.id === boundSessionRef.current && !viewingOtherSession) setMembership(membershipOf(listedMembership));
  }, [listedMembership]); // eslint-disable-line react-hooks/exhaustive-deps
  const guest = membership?.isOwner === false;
  agentPausedRef.current = membership?.agentPaused === true;
  const canStop = control?.canStop === true;
  const attachments = useSyncExternalStore(draft.subscribe, draft.getAttachments, draft.getServerAttachments);
  const recoverySnapshot = useSyncExternalStore(draft.subscribe, () => JSON.stringify({ rows: draft.getRecalledInputs(), reply: draft.getReplyTarget() }), () => '{"rows":[],"reply":null}');
  const recovery = useMemo(() => JSON.parse(recoverySnapshot) as { rows: BrainRecalledInput[]; reply: { id: string } | null }, [recoverySnapshot]);
  const setAttachments = draft.setAttachments;
  const recalledRevisionRef = useRef(0);
  const restoreRecalled = (session: string | undefined, rows: readonly BrainRecalledInput[]): void => {
    if (!session || session !== boundSessionRef.current) return;
    recalledRevisionRef.current += 1;
    draft.recall(session, rows);
  };
  const [uploads, setUploads] = useState<PendingUpload[]>([]);
  const uploadKeyRef = useRef(0);
  const uploadControllers = useRef(new Map<number, AbortController>());
  // The model catalog (lazily fetched, RBAC-filtered server-side) — the single source shared by the header
  // ModelPicker, which renders it through ModelOptionList.
  const [models, setModels] = useState<BrainModelOption[] | null>(null);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [modelsError, setModelsError] = useState(false);
  const [currentModel, setCurrentModel] = useState('');
  const [fast, setFast] = useState(false);
  const conversationName = nameDraft.session === activeSessionId ? nameDraft.name : '';
  const setConversationName = (name: string) => setNameDraft({ session: activeSessionId, name });
  const [conversationIcon, setConversationIcon] = useState<BrainSessionIdentity['icon']>('');
  const [provider, setProvider] = useState('');
  const [providerLabel, setProviderLabel] = useState('');
  const [usageProvider, setUsageProvider] = useState('');
  /** Provider display and subscription identity always move together; never key usage on a public id. */
  const applyProviderIdentity = useCallback((identity: { provider?: string; providerLabel?: string; usageProvider?: string; icon?: BrainSessionIdentity['icon'] }) => {
    // Partial model events carry provider identity only; preserve the conversation's saved icon.
    if (identity.icon !== undefined) setConversationIcon(identity.icon);
    setProvider(identity.provider ?? '');
    setProviderLabel(identity.providerLabel ?? '');
    setUsageProvider(identity.usageProvider ?? '');
  }, []);
  const reasoning = useReasoningLevel(activeSessionId, `${provider}/${currentModel}`);
  const [modelStatus, setModelStatus] = useState<SaveStatus>('idle');
  const [modelQueued, setModelQueued] = useState(false);
  const modelAttemptRef = useRef<ModelSwitchAttempt | null>(null);
  // The attempt whose answer was lost: it may have switched the model, so no other pick is sent until it is read back.
  const modelHeldRef = useRef<ModelSwitchAttempt | null>(null);
  const childFocusRef = useRef(childFocus);
  childFocusRef.current = childFocus;
  const seenModelEventsRef = useRef(new Set<string>());
  const latestModelRef = useRef<BrainModelOption | null>(null);
  const modelQueueRef = useRef<Promise<unknown>>(Promise.resolve());
  const [focusNonce, setFocusNonce] = useState(0);
  const [sendNonce, setSendNonce] = useState(0);
  useEffect(() => {
    setModelStatus('idle');
    setModelQueued(false);
    modelAttemptRef.current = null;
    modelHeldRef.current = null;
    seenModelEventsRef.current.clear();
    latestModelRef.current = null;
  }, [activeSessionId]);
  // The attempt settles once, whichever signal arrives first (the HTTP answer or the stream's event), and a
  // failure is reported by that one settlement: one card per conversation's switch, replaced by the next attempt.
  const settleModelAttempt = (attempt: ModelSwitchAttempt, outcome: 'saved' | 'error', failure?: string): void => {
    if (modelAttemptRef.current !== attempt || attempt.session !== boundSessionRef.current || attempt.outcome) return;
    attempt.outcome = outcome;
    if (modelHeldRef.current === attempt) modelHeldRef.current = null;
    setModelQueued(false);
    setModelStatus(outcome);
    const key = `model-switch:${attempt.session}`;
    if (outcome === 'error') toast(failure ?? t.common.actionFailed, 'error', { key });
    else dismissKey(key);
  };
  // Durable snapshots and live events use the same completion seam. Remember event ids before a retry so
  // replaying its earlier failure cannot settle the new request for the same provider/model pair.
  const reconcileModelEvent = (event: ChatEventView): void => {
    const details = event.details;
    if (details.kind !== 'model' && !(details.kind === 'error' && details.notice?.code === 'model-switch-failed')) return;
    if (details.kind === 'model') hydrationStampRef.current.model += 1;
    seenModelEventsRef.current.add(event.id);
    const attempt = modelAttemptRef.current;
    if (!attempt?.started || attempt.outcome || attempt.previousEvents.has(event.id)) return;
    const m = attempt.selection;
    if (details.kind === 'model' && details.model === m.model && details.provider === m.provider) {
      settleModelAttempt(attempt, 'saved');
      if (attempt.outcome === 'saved') {
        setCurrentModel(details.model);
        applyProviderIdentity({ provider: details.provider, providerLabel: m.providerLabel });
      }
    } else if (details.kind === 'error' && details.notice?.code === 'model-switch-failed' && details.notice.model === m.model
      && (!details.notice.provider || details.notice.provider === m.provider)) {
      settleModelAttempt(attempt, 'error', interpolate(t.brainChat.eventErrorModelSwitchFailed, {
        error: details.notice.reason === 'native-checkpoint-conversion' ? t.brainChat.nativeCheckpointConversionFailed : details.text,
      }));
    }
  };
  // A render-only mirror, unknown until this conversation's authoritative status/event arrives.
  const [workMode, setControlModeState] = useState<BrainWorkMode>();
  const workModeRef = useRef<BrainWorkMode>(undefined);
  const setControlMode = (mode: BrainWorkMode | undefined): void => {
    workModeRef.current = mode;
    setControlModeState(mode);
  };
  const applyControl = (next: BrainStreamControl): void => {
    hydrationStampRef.current.control += 1;
    // A run invalidates the last exchange before its user echo arrives. Idle control-only frames
    // do not change the transcript head and must not discard its terminal status read.
    if (next.streaming) {
      transcriptEventRevision.current += 1;
      setReissueTarget(null);
    }
    setControl(next);
    if (!next.streaming) { writerRead.current += 1; setTurnWriter(null); }
    setModelRetry(next.modelRetry);
    setAsk(next.pendingAsk);
    setControlMode(next.workMode);
    setPendingPlan(next.pendingPlan);
  };
  const [modeSwitching, setModeSwitching] = useState(false);
  const modeSwitchRef = useRef<Promise<void> | null>(null);
  const [pendingPlan, setPendingPlan] = useState<BrainPendingPlan | null>(null);
  const [renameOpen, setRenameOpen] = useState(false);
  // The conversation switcher, opened from every surface and rendered once by the provider below.
  const [historyOpen, setHistoryOpen] = useState(false);
  // Lives on the controller (mounted once) rather than the surface, so the choice survives the dock's
  // Chat↔Terminál toggle and every route change, exactly like the transcript itself.
  const [thoughts, setThoughts] = usePersistentState<'show' | 'hide'>('elowen.chat.thoughts', 'hide', THOUGHTS_VALUES);

  // --- Session binding (mirror BrainClient): stable per-tab clientId, monotonic generation, bound id. ---
  const clientIdRef = useRef<string>('');
  const clientId = (): string => clientIdRef.current || (clientIdRef.current = getBrainClientId());
  /** Highest start generation issued this tab (mirror BrainClient.startGeneration). */
  const genRef = useRef(0);
  /** The generation that committed `boundSession` (mirror BrainClient.boundGeneration). */
  const boundGenRef = useRef<number | undefined>(undefined);
  /** The conversation this controller is bound to (mirror BrainClient.bound). */
  const boundSessionRef = useRef<string | undefined>(undefined);
  /** Delegations of the bound conversation already announced as running, so a progress tick is not a start. */
  const runningChildrenRef = useRef(new Set<string>());
  /** Cancel unfinished uploads on rebind; completed attachments stay with their session's draft. */
  const bindSession = (sessionId: string): void => {
    if (boundSessionRef.current !== sessionId) {
      runningChildrenRef.current.clear();
      for (const controller of uploadControllers.current.values()) controller.abort();
      uploadControllers.current.clear();
      setUploads([]);
    }
    draft.bindSession(sessionId);
    boundSessionRef.current = sessionId;
  };
  /** A current stream boundary that has rendered and may acknowledge the latest visible activity row. */
  const activityBoundaryRef = useRef<{ sessionId: string; generation: number; refreshed: boolean; renderedSeq: number | null } | null>(null);
  const activityAckInFlightRef = useRef<string | null>(null);
  const activityAckedRef = useRef(new Map<string, number>());
  const [activityBoundaryVersion, setActivityBoundaryVersion] = useState(0);
  const [activityWakeVersion, setActivityWakeVersion] = useState(0);
  useEffect(() => {
    const wake = () => {
      const session = boundSessionRef.current;
      const generation = genRef.current;
      if (!session || !attachedRef.current || viewingOtherSession) return;
      // Another tab may have advanced this viewer's read cursor while this tab was blurred.
      void elowenClient.brainStatus(session).then(status => {
        if (generation !== genRef.current || session !== boundSessionRef.current) return;
        setConversationReadState(current => current && status.conversationReadState && current.revision > status.conversationReadState.revision ? current : status.conversationReadState);
        setActivityWakeVersion(version => version + 1);
      }).catch(() => {
        if (generation === genRef.current && session === boundSessionRef.current) toast(t.brainChat.statusReadError, 'error');
      });
    };
    window.addEventListener('focus', wake);
    return () => window.removeEventListener('focus', wake);
  }, [t.brainChat.statusReadError, toast, viewingOtherSession]);
  const markActivityBoundary = (sessionId: string | undefined, seq?: number): void => {
    if (!sessionId) return;
    const cachedSeq = sessions.data?.find((session) => session.id === sessionId)?.activity?.seq;
    const boundary = { sessionId, generation: genRef.current, refreshed: false, renderedSeq: seq ?? cachedSeq ?? null };
    activityBoundaryRef.current = boundary;
    void qc.invalidateQueries({ queryKey: ['brain-sessions'] }).then(() => {
      if (activityBoundaryRef.current !== boundary) return;
      boundary.refreshed = true;
      setActivityBoundaryVersion((value) => value + 1);
    });
  };
  /** ensureAttached idempotency: once true the stream stays live for the tab's life. */
  const attachedRef = useRef(false);
  /** The last snapshot arrived with a truncated run journal: part of the STILL RUNNING turn was dropped by
   *  the bounded buffer. Durable history only becomes authoritative once the turn settles, so the refetch
   *  waits for the terminal event rather than replacing a live turn with a half-written one. */
  const truncatedPendingRef = useRef(false);
  /** Per-field stream freshness. Status starts before the stream, so any overlapping frame that lands first
   *  is newer truth for that field and must not be overwritten by the older read. */
  const hydrationStampRef = useRef({ session: 0, model: 0, control: 0, cards: 0, processes: 0, queue: 0, project: 0, rail: 0, typing: 0, reissue: 0, readState: 0 });
  /** Two reads publish the same server fact (the reissue target), so the ORDER of their answers decides it.
   *  Every read takes a number, and only a read newer than the one already published may answer: a slow
   *  read issued before the turn settled cannot erase the target a later read already delivered. */
  const reissueReadRef = useRef(0);
  const reissuePublishedRef = useRef(0);
  /** The read cursor has the same problem: a settling turn emits several status reads and only their order
   *  decides which answer stands. A snapshot that STATES a cursor also fences the reads started before it. */
  const readStateReadRef = useRef(0);
  const readStatePublishedRef = useRef(0);
  const modelReadRef = useRef(0);
  const modelPublishedRef = useRef(0);

  /** Every status read can recover unknown control after a failed rebind, without a retry loop.
   * Session, generation and stream revision still own freshness; known control uses its existing readers. */
  const readStatus = async (sessionId = boundSessionRef.current): Promise<BrainStatus> => {
    const generation = genRef.current;
    const reissueRead = ++reissueReadRef.current;
    const stamp = hydrationStampRef.current.control;
    const railStamp = ++hydrationStampRef.current.rail;
    const sessionStamp = hydrationStampRef.current.session;
    const typingStamp = hydrationStampRef.current.typing;
    const reissueStamp = hydrationStampRef.current.reissue;
    const readStateStamp = hydrationStampRef.current.readState;
    const readStateRead = ++readStateReadRef.current;
    const yoloStamp = ++yoloRead.current;
    const transcriptRevision = transcriptEventRevision.current;
    const fresh = (): boolean => generation === genRef.current && sessionId === boundSessionRef.current
      && stamp === hydrationStampRef.current.control;
    try {
      const status = await elowenClient.brainStatus(sessionId);
      // Every selected-session status read replaces plugin data. Project selection and newer reads fence
      // older payloads independently of control events and snapshot-only execution identity hydration.
      if (generation === genRef.current && sessionId === boundSessionRef.current
        && sessionStamp === hydrationStampRef.current.session && railStamp === hydrationStampRef.current.rail) {
        setTelemetry(current => ({ ...current, chatRailSections: status.chatRailSections ?? [] }));
      }
      // Stream control frames do not carry YOLO; they must not discard this status-only fact.
      if (generation === genRef.current && sessionId === boundSessionRef.current && yoloStamp === yoloRead.current) {
        setYolo(status.yolo ?? null);
      }
      // Reissue belongs to this transcript head, not to independent control changes. A newer
      // run, message, snapshot or navigation fences it while an idle control frame does not. The stamp
      // covers the snapshot that STATES the target after launching a read of its own: that read answers
      // for usage, and must not overwrite the target the snapshot is about to publish. A read issued
      // while the daemon was still settling the turn answers null, so only a read newer than the one
      // already published may publish: its answer describes at least as recent a server state.
      if (generation === genRef.current && sessionId === boundSessionRef.current
        && transcriptRevision === transcriptEventRevision.current
        && reissueStamp === hydrationStampRef.current.reissue
        && reissueRead > reissuePublishedRef.current) {
        reissuePublishedRef.current = reissueRead;
        setReissueTarget(status.reissueTarget);
      }
      if (fresh()) {
        setTurnWriter(status.turnWriter ?? null);
      }
      // The read cursor is its own monotonic fact (its revision) with its own order, never a control-frame
      // fact: the trailing control frames a settling turn emits must not drop the boundary a reader scrolled
      // up needs for the jump control, while a snapshot that stated a cursor still fences an older read.
      if (generation === genRef.current && sessionId === boundSessionRef.current
        && readStateStamp === hydrationStampRef.current.readState && readStateRead > readStatePublishedRef.current) {
        readStatePublishedRef.current = readStateRead;
        // An answer without a cursor leaves this viewer with none, never with an absent value: the surface
        // distinguishes "no personal cursor" (null) from a rendered one.
        setConversationReadState(current => current && status.conversationReadState && current.revision > status.conversationReadState.revision ? current : status.conversationReadState ?? null);
      }
      if (fresh()) {
        if (typingStamp === hydrationStampRef.current.typing) setTyping(status.typing);
        if (workModeRef.current === undefined) applyControl(status);
      }
      return status;
    } catch (error) {
      if (fresh() && workModeRef.current === undefined) toast(t.brainChat.statusReadError, 'error');
      throw error;
    }
  };

  const { usage, fenceUsage, startUsageRead, setUsage, setUsageIfFresh, applyUsageSnapshot, refreshUsage } = useBrainChatUsage({
    generationRef: genRef,
    sessionRef: boundSessionRef,
    readStatus,
  });

  /** Fence execution identity without dropping instance-wide MCP. Plugin sections have one reader above. */
  const applyTelemetry = (st: BrainStatus, projectFresh: boolean): void => {
    setTelemetry((current) => (projectFresh
      ? { ...current, ...telemetryOf(st) }
      : { ...current, ...telemetryOf(st), projectRef: current.projectRef, project: current.project }));
  };

  const [projectMoves, setProjectMoves] = useState<ReadonlyMap<string, { status: SaveStatus; retryTarget: ProjectExecutionRef | null }>>(new Map());
  const projectMovesRef = useRef(projectMoves);
  const setProjectMove = (session: string, next: { status: SaveStatus; retryTarget: ProjectExecutionRef | null } | null): void => {
    const map = new Map(projectMovesRef.current);
    if (next === null) map.delete(session); else map.set(session, next);
    projectMovesRef.current = map;
    setProjectMoves(map);
  };
  const reportedProjectKey = telemetry.projectRef ? executionRefKey(telemetry.projectRef) : '';
  // A failed (not lost) move offers a retry only while the daemon still reports the identity it failed from.
  useEffect(() => {
    const kept = [...projectMovesRef.current].filter(([, move]) => move.status !== 'error');
    if (kept.length !== projectMovesRef.current.size) { projectMovesRef.current = new Map(kept); setProjectMoves(projectMovesRef.current); }
  }, [reportedProjectKey, activeSessionId]);
  const selectProjectExecution: BrainChatValue['selectProjectExecution'] = async (target, session) => {
    if (boundSessionRef.current !== session) return;
    const held = projectMovesRef.current.get(session);
    // Choosing what the conversation already runs in writes nothing; a running or unknown move is not stacked on.
    if (held?.status === 'saving' || held?.status === 'unconfirmed') return;
    if (telemetry.projectRef && executionRefKey(target) === executionRefKey(telemetry.projectRef)) return;
    const key = `project-move:${session}`;
    dismissKey(key);
    setProjectMove(session, { status: 'saving', retryTarget: target });
    const generation = genRef.current;
    try {
      const response = await elowenClient.brainSetExecution(target, session);
      setProjectMove(session, null);
      if (generation !== genRef.current || boundSessionRef.current !== session) return;
      // Empty conversations emit no transcript cwd event. The mutation response is already authoritative;
      // publish it for every consumer and fence status reads started before confirmation.
      hydrationStampRef.current.project += 1;
      hydrationStampRef.current.rail += 1;
      setTelemetry((current) => ({ ...current, projectRef: response.projectRef, project: null, chatRailSections: [] }));
      return response;
    } catch (error) {
      // A selection whose answer was lost may have been applied: nothing is offered again until the daemon's own
      // identity has been read, and a read that fails keeps the hold.
      const unknown = holdsAnswer(error);
      setProjectMove(session, { status: unknown ? 'unconfirmed' : 'error', retryTarget: target });
      reportFailure(key, error, {
        message: typedRefusalMessage(error) ?? t.brainChat.projectPickerFailed,
        reload: releasing(() => rereadProjectExecution(session), () => setProjectMove(session, null)),
      });
    }
  };
  const rereadProjectExecution: BrainChatValue['rereadProjectExecution'] = async (session) => {
    // The read is for the conversation whose move was lost, whichever one is on screen now: a held move releases
    // only after its own conversation has been read. What the read shows reaches the screen only while that
    // conversation is still the bound one.
    const generation = genRef.current;
    // Only a read for the conversation on screen fences the hydration of that conversation's own reads.
    const stamp = boundSessionRef.current === session ? ++hydrationStampRef.current.project : -1;
    const status = await elowenClient.brainStatus(session);
    if (generation !== genRef.current || boundSessionRef.current !== session || stamp !== hydrationStampRef.current.project) return;
    hydrationStampRef.current.rail += 1;
    setTelemetry((current) => ({ ...current, projectRef: status.projectRef ?? null, project: null, chatRailSections: [] }));
  };
  /** How long the stream may stay silent before it counts as dead, in either phase — operator-tunable
   *  (`runtime.limits`), floored at the heartbeat interval, and falling back to the built-in defaults until
   *  the config arrives, so a daemon that never answers behaves exactly as before. */
  const silence = useMemo(() => resolveStreamSilence(config?.runtime?.limits), [config?.runtime?.limits]);
  const silenceRef = useRef(silence);
  silenceRef.current = silence;
  const stream = useBrainChatStream({
    getGeneration: () => genRef.current,
    loadPolicy: loadConnectionPolicy,
    getPolicy: getConnectionPolicy,
    setReady,
    setReconnecting,
    onConnectionState: (state, firstAttempt) => {
      setConnectionState(state);
      setConnectionFirstAttempt(firstAttempt);
      setConnectionFailure(null);
    },
    onFailure: connectionFailed,
  });
  useEffect(() => {
    const retire = (): void => {
      genRef.current += 1;
      windowRequest.current += 1;
      setMessageWindow(null);
      draft.clearReplies();
      setTurnWriter(null);
    };
    const off = subscribeAuthTransitions(transition => { if (!transition.refresh) retire(); });
    window.addEventListener(AUTH_CLEARED_EVENT, retire);
    return () => { off(); window.removeEventListener(AUTH_CLEARED_EVENT, retire); };
  }, [draft]);

  const nextGeneration = (): number => {
    setSubmittedRun(null);
    setTurnWriter(null);
    writerRead.current += 1;
    setReissueTarget(null);
    setYolo(null);
    setConversationReadState(null);
    setTyping([]);
    windowRequest.current += 1;
    setMessageWindow(null);
    return genRef.current += 1;
  };
  const binding = (): BrainBinding => buildBinding(boundSessionRef.current, boundGenRef.current, clientId());
  const isCurrentBinding = (confirmed: BrainBinding): boolean =>
    confirmed.session === boundSessionRef.current && confirmed.client === clientId()
    && confirmed.generation === genRef.current && confirmed.generation === boundGenRef.current;
  const requireCurrentBinding = (confirmed: BrainBinding | null): BrainBinding => {
    if (!confirmed || !isCurrentBinding(confirmed)) throw new Error('Conversation creation was superseded.');
    return confirmed;
  };
  const [pageVisible, setPageVisible] = useState(() => typeof document !== 'undefined' && !document.hidden);
  useEffect(() => {
    const update = () => setPageVisible(!document.hidden);
    document.addEventListener('visibilitychange', update);
    return () => document.removeEventListener('visibilitychange', update);
  }, []);
  const typingEmitter = useConversationTyping({
    session: activeSessionId, client: activeSessionId ? clientId() : null,
    generation: boundGenRef.current ?? null, ready: ready && !readOnly && !childFocus,
    shared: !!membership?.participants.length, visible: pageVisible && hasSurface,
    send: elowenClient.brainTyping,
  });
  const bumpFocus = (): void => setFocusNonce((n) => n + 1);

  // A snapshot whose run journal had overflowed left the transcript possibly missing part of that turn.
  // At the terminal boundary (idle, or an error frame ending a turn that never settled) the durable
  // history is authoritative again, so replace the view from it — the alternative is a silently
  // inconsistent transcript no later event would ever correct.
  const repairTruncatedHistory = (): void => {
    if (!truncatedPendingRef.current) return;
    truncatedPendingRef.current = false;
    void loadHistory(genRef.current).catch(() => toast(t.brainChat.historyLoadError, 'error'));
  };

  // Boot (resume) the brain, load history, open the stream — bound to the conversation start() resolves.
  // Runs on initial attachment and explicit selection, never transport recovery. `opts` selects the
  // requested conversation (default: the caller's active one). Superseded responses are discarded.
  const connect = async (opts: { session?: string; fresh?: boolean; target?: ProjectExecutionRef } = {}): Promise<BrainBinding | null> => {
    stream.close();
    setReadOnlyView(null);
    setConnectionState(null);
    setConnectionFailure(null);
    setReady(false);
    setNotice(''); // a fresh connection (mount / session switch) starts without a stale runtime line
    setAsk(null); // drop any parked question from the previous conversation
    setPluginPicker(null); // a picker belongs to the conversation being left
    setPendingPlan(null); // a pending plan belongs to the conversation being left; the decided key derives from the session id and follows the rehydration below
    setProcesses([]);
    setCards([]); // and any cards from the previous conversation
    setQueued([]); // and any pending mid-turn queue from the previous conversation
    setRemovingQueue(new Set()); // its in-flight removes name ids that mean something else here
    setGoal(null); setPromptSuggestion(null); // a goal belongs to ONE conversation; the snapshot frame hydrates this one's own
    const generation = nextGeneration();
    const previousSession = boundSessionRef.current;
    const issuedIn = authGeneration();
    let started: Awaited<ReturnType<typeof elowenClient.brainStart>>;
    try { started = await elowenClient.brainStart(opts, { client: clientId(), generation }); }
    catch (error) {
      if (generation === genRef.current && issuedIn === authGeneration()) connectionFailed('start', error);
      throw error;
    }
    if (generation !== genRef.current || issuedIn !== authGeneration()) return null; // a newer connect/switch superseded this one
    // Commit the binding only when still current (out-of-order A/B switch guard, mirror BrainClient :168).
    bindSession(started.sessionId);
    boundGenRef.current = generation;
    const confirmed = binding();
    setActiveSessionId(started.sessionId);
    // The stream's snapshot frame hydrates the transcript (see the `snapshot` listener), so there is no
    // history fetch here. The view is cleared up front only when what it currently shows does NOT belong to
    // the conversation being connected — another conversation, or a read-only preview of a foreign session.
    // A plain reconnect keeps its turns on screen until the frame replaces them, with no blank flash.
    if (readOnly || (previousSession && previousSession !== started.sessionId)) {
      setControl(null);
      setModelRetry(null);
      setView(emptyView());
      // The older-history cursor names an entry of the conversation just left. Until this one's snapshot frame
      // replaces the window, the sentinel of the emptied view would ask THIS session for that page (HTTP 409).
      clearHistoryWindow();
    }
    // The Project belongs to the conversation just left; the snapshot or the status read below names this
    // one's. Keeping it would hand the chat dock the previous conversation's Project until then, and for good
    // when that read fails.
    if (previousSession !== started.sessionId) {
      setTelemetry((current) => ({ ...current, projectRef: null, project: null, chatRailSections: [] }));
      setMembership(null);
      setTurnWriter(null);
    }
    // Status and the atomic history snapshot are independent reads. Start status now, but open the stream
    // immediately: status includes cumulative descendant usage and used to hold the transcript empty for
    // seconds before the browser was even allowed to request its first history frame.
    const statusHydrationStamp = { ...hydrationStampRef.current };
    const statusModelRead = ++modelReadRef.current;
    const statusRecalledRevision = recalledRevisionRef.current;
    const statusUsageRead = startUsageRead();
    const status = readStatus(boundSessionRef.current).catch(() => null);
    // A durable state change (model, title, working directory, mode) keeps the same stream: status is
    // refreshed without reconnecting, and usage stays fenced against a newer stream event.
    const refreshSettledState = (): void => {
      const modelStamp = hydrationStampRef.current.model;
      const modelRead = ++modelReadRef.current;
      const projectStamp = ++hydrationStampRef.current.project;
      const controlStamp = ++hydrationStampRef.current.control;
      const session = boundSessionRef.current;
      const usageRead = startUsageRead();
      void readStatus(boundSessionRef.current)
        .then((fresh) => {
          if (generation !== genRef.current || session !== boundSessionRef.current) return;
          setUsageIfFresh(fresh.usage, usageRead);
          if (session === boundSessionRef.current) {
            applyTelemetry(fresh, projectStamp === hydrationStampRef.current.project);
            if (controlStamp === hydrationStampRef.current.control) applyControl(fresh);
          }
          setLineCfg(fresh.statusline);
          if (modelStamp === hydrationStampRef.current.model && modelRead > modelPublishedRef.current) {
            modelPublishedRef.current = modelRead;
            setCurrentModel(fresh.model);
            setFast(fresh.fast === true);
            applyProviderIdentity(fresh);
          }
        })
        .catch(() => { /* best-effort */ });
    };
    await stream.openLive({
      generation,
      session: started.sessionId,
      client: clientId(),
      boundGeneration: boundGenRef.current,
      handlers: {
        event: applyEvent,
        snapshotStart: () => setNotice(''),
        snapshot: (snap) => {
          transcriptEventRevision.current += 1;
          // A rebind this stream never saw moved the binding server-side. Follow it so lazy-load
          // and every later send name the current conversation.
          if (snap.sessionId && snap.sessionId !== boundSessionRef.current) {
            hydrationStampRef.current.session += 1;
            setTelemetry(current => ({ ...current, chatRailSections: [] }));
            setMembership(null);
            bindSession(snap.sessionId);
            setActiveSessionId(snap.sessionId);
          }
          const folded = fromSnapshot(snap);
          // The snapshot fences older REST reads even without a usage-bearing event in its journal.
          if (!applyUsageSnapshot(snap.events)) {
            // The connect-time status sample predates this boundary and was intentionally fenced above. A
            // journal with no step/idle does not imply zero usage, so refresh usage alone from the session
            // the snapshot just authoritatively bound; every other status field remains owned by the original
            // hydration read below.
            void refreshUsage()
              .catch(() => { /* best-effort; the next live usage event can still hydrate it */ });
          }
          // The atomic control wins over every older control event in the replay tail.
          const control = snap.control;
          const streaming = control.streaming;
          settlementDirty.current = folded.turns.some(turn => (turn.role === 'you' || turn.role === 'elowen') && !!turn.id && !turn.settled);
          // The snapshot replaces the newest window; older pages the reader loaded stay when still valid.
          replaceNewestWindow(() => ({ ...folded, thinking: streaming }), snap.nextBefore ?? null, snap.hasMore ?? false);
          // Latest matching terminal event wins at the replay boundary; already-seen failures are ignored.
          for (const turn of [...folded.turns].reverse()) {
            if (turn.role === 'event') for (const item of [...turn.events].reverse()) reconcileModelEvent(item.event);
          }
          const attempt = modelAttemptRef.current;
          if (attempt?.started && snap.session?.model === attempt.selection.model
            && snap.session.provider === attempt.selection.provider) settleModelAttempt(attempt, 'saved');
          if (snap.session) {
            hydrationStampRef.current.model += 1;
            setCurrentModel(snap.session.model);
            applyProviderIdentity(snap.session);
            setFast(snap.session.fast);
            // The snapshot is newer truth for the execution identity than a status read still in flight.
            hydrationStampRef.current.project += 1;
            const session = snap.session;
            restoreRecalled(boundSessionRef.current, session.recalled ?? []);
            setTelemetry((current) => ({ ...current, projectRef: session.projectRef ?? null, project: session.project ?? null }));
            setMembership(membershipOf(session));
            setTurnWriter(session.turnWriter ?? null);
            hydrationStampRef.current.reissue += 1;
            setReissueTarget(session.reissueTarget);
            hydrationStampRef.current.readState += 1;
            setConversationReadState(current => current && session.conversationReadState && current.revision > session.conversationReadState.revision ? current : session.conversationReadState ?? null);
            hydrationStampRef.current.typing += 1;
            setTyping(session.typing);
          } else {
            hydrationStampRef.current.readState += 1;
            setConversationReadState(null);
            hydrationStampRef.current.reissue += 1;
            setReissueTarget(null);
            setTyping([]);
            setTurnWriter(null);
          }
          if (Object.prototype.hasOwnProperty.call(snap, 'cards')) {
            hydrationStampRef.current.cards += 1;
            setCards(withoutBackgroundProcessCards(snap.cards ?? []));
          }
          hydrationStampRef.current.processes += 1;
          setProcesses(snap.processes ?? []);
          // Queue state lives outside the transcript fold. Replay the latest server projection, including
          // compaction-pending admission chips, and fence the older connect-time status read.
          const replayedQueue = snap.events.findLast(event => event.type === 'queue');
          if (replayedQueue) {
            hydrationStampRef.current.queue += 1;
            setRemovingQueue(new Set());
            void refreshQueue().catch(error => toast(typedRefusalMessage(error) ?? t.brainChat.queueRemoveError, 'error'));
          }
          // Explicit nulls matter here: the snapshot can clear a question or plan that another surface settled.
          applyControl(control);
          // Goal outlives the journal. Presence distinguishes an older daemon from an explicit cleared goal.
          if (Object.prototype.hasOwnProperty.call(snap, 'goal')) setGoal(snap.goal ?? null);
          setPromptSuggestion(snap.promptSuggestion ?? null);
          truncatedPendingRef.current = streaming && snap.truncated === true;
          markActivityBoundary(boundSessionRef.current, snap.activitySeq);
        },
        text: () => setNotice(''),
        notice: (message, done) => setNotice(done ? '' : message),
        error: (message) => {
          setNotice(message);
          // Preserve the independent wait/model lanes while the replacement snapshot is pending.
          hydrationStampRef.current.control += 1;
          // Folding the error ends the streaming turn and its tool/thinking indicators. A successful
          // reconnect replaces it from durable history.
          repairTruncatedHistory();
        },
        session: (sessionId) => {
          // Rebind without changing generation. The fresh conversation is rebuilt solely from stream events.
          hydrationStampRef.current.session += 1;
          setTelemetry(current => ({ ...current, chatRailSections: [] }));
          setMembership(null);
          setTurnWriter(null);
          hydrationStampRef.current.control += 1;
          setAsk(null);
          setPendingPlan(null);
          setControlMode(undefined);
          setControl(null);
          setModelRetry(null);
          hydrationStampRef.current.cards += 1;
          bindSession(sessionId);
          setActiveSessionId(sessionId);
          setUsage(null); // previous-session samples cannot hydrate the newly bound conversation
          const stamp = hydrationStampRef.current.control;
          void readStatus(sessionId).then(status => {
            if (generation !== genRef.current || boundSessionRef.current !== sessionId
              || stamp !== hydrationStampRef.current.control) return;
            applyControl(status);
          }).catch(() => { /* readStatus reports the scoped status-read failure */ });
          setCards([]);
          setProcesses([]);
          setGoal(null); setPromptSuggestion(null);
          // Close the lazy-load window and bump its epoch so an older page cannot duplicate the new session.
          clearHistoryWindow();
          setNotice(t.brainChat.freshConversation);
          void qc.invalidateQueries({ queryKey: ['brain-sessions'] });
        },
        subagent: (subagent) => {
          // The switcher's running-agents count reads the same branch, and that read has no poll: a
          // delegation that starts mid-turn has to ask for it once. Progress ticks of a child already
          // announced do not, and a settle (below) forgets the child so a steered rerun announces again.
          if (subagent.status === 'running') {
            if (!runningChildrenRef.current.has(subagent.sessionId)) {
              runningChildrenRef.current.add(subagent.sessionId);
              void qc.invalidateQueries({ queryKey: QUERY_KEYS.brainConversationLinks });
            }
          } else runningChildrenRef.current.delete(subagent.sessionId);
          // Child usage is persisted before terminal progress. Refresh immediately, fenced so a late read
          // cannot overwrite a newer step or idle snapshot.
          if (subagent.status !== 'running') {
            void refreshUsage()
              .catch(() => { /* best-effort */ });
            // A delegation that just settled is a row of the switcher's sub-agent branch, and its status
            // there comes from the durable lifecycle rather than from this event. Refresh the branch read
            // instead of trying to fold a live event into a cached tree. Only on a start or a settle: a
            // running child emits progress continuously, and invalidating on each tick would be a poll.
            void qc.invalidateQueries({ queryKey: QUERY_KEYS.brainConversationLinks });
          }
        },
        workflow: (workflow) => {
          // Same reasoning as above, and the same restraint: the whole DAG re-fans on every node's tool
          // event, so only a terminal snapshot refreshes the branch.
          if (workflow.status !== 'running') void qc.invalidateQueries({ queryKey: QUERY_KEYS.brainConversationLinks });
        },
        goal: setGoal,
        promptSuggestion: setPromptSuggestion,
        // The generated name landed after the provisional one. The rail's registry query is the one live
        // owner of conversation titles, so invalidate it exactly like a manual rename does — no transcript
        // change, no reconnect, no local title copy. Session-agnostic key, so no session fence is needed.
        title: () => { void qc.invalidateQueries({ queryKey: ['brain-sessions'] }); },
        settings: () => refreshSettledState(),
        process: (snapshot) => { hydrationStampRef.current.processes += 1; setProcesses(snapshot); },
        card: (card) => {
          if (isBackgroundProcessCardId(card.id)) return;
          hydrationStampRef.current.cards += 1;
          setCards((cur) => upsertCard(cur, card));
        },
        // A queue frame supersedes every optimistic removal still in flight.
        queue: () => {
          hydrationStampRef.current.queue += 1;
          setRemovingQueue(new Set());
          void refreshQueue().catch(error => toast(typedRefusalMessage(error) ?? t.brainChat.queueRemoveError, 'error'));
        },
        // A cancelled just-sent turn is removed by durable id and restored only into an empty composer.
        discardUser: ({ text }) => {
          if (text) { setInput((current) => (current.trim() ? current : text)); bumpFocus(); }
        },
        // Compaction is announced outside an open agent run, but a following turn can start before a
        // separate history read returns. Cold-start compaction is followed immediately by the user echo
        // (turnRunner), and a terminal compaction can be followed by a queued turn. Reconnect through the
        // resync path for one atomic history/replay/control snapshot; replayed compacted frames do not
        // invoke this live handler. Fence the previous usage epoch before reconnect status hydrates it.
        compacted: mode => {
          ++hydrationStampRef.current.control;
          setControlMode(mode); setPendingPlan(null);
          fenceUsage();
          stream.reconnectNow();
        },
        // A persisted non-trigger append uses this same atomic seam: a user echo would settle the live
        // assistant tail and start thinking even though the append starts no turn.
        // Boot recovery finished under this stream (the daemon restarted and this tab reattached in the
        // window before its state was recovered): everything this client was shown in that window has to be
        // rebuilt. RECONNECT rather than refetch history, because the frame can land MID-TURN — the recovery
        // wave resumes parked owner conversations concurrently with the `workflows` provider that triggers
        // this broadcast (src/brain/recovery/providers.ts), and the user can send the moment the port is
        // back. Durable history cannot describe a running turn: its rows stay provisional until the run
        // settles and the paged read serves settled rows only, so re-deriving the transcript from storage
        // deleted the live tail exactly as a mid-turn marker refetch did. The stream snapshot is the one read
        // model that carries the rewritten history AND the running turn's journal on a single server tick,
        // which is precisely what `resync` asks for (see brainService.publishResync). A resync is broadcast
        // and never journaled, so the fresh stream cannot deliver it again.
        //
        // Through the stream's own reconnect controller, never a bare `connect()`: the daemon broadcasts this
        // frame to every attached conversation on one synchronous pass, and the silence watchdog can fire in
        // the same window — the controller is what turns those into ONE attempt with backoff instead of
        // racing POST reattachments or independent retry owners.
        resync: () => stream.reconnectNow(),
        // A stored chat event folds into the transcript (see the `chat-event` fold for why it is never a
        // refetch). A changed setting or a delegated job's finish also refreshes the status the bar shows,
        // without reconnecting; usage stays fenced against a newer stream event.
        chatEvent: (event) => {
          reconcileModelEvent(event);
          if (event.details.kind === 'mode') {
            hydrationStampRef.current.control += 1;
            setControlMode(event.details.mode);
            if (event.details.mode !== 'plan') setPendingPlan(null);
          }
          if (changesChatStatus(event.details)) refreshSettledState();
        },
        // Ask stays visible until the daemon resolves this exact id; idle alone cannot prove it is settled.
        ask: ({ id, questions, kind }) => {
          hydrationStampRef.current.control += 1;
          setAsk({ id, questions, kind });
        },
        askResolved: (id) => {
          hydrationStampRef.current.control += 1;
          setAsk((cur) => (cur && cur.id === id ? null : cur));
        },
        step: (nextUsage) => { if (nextUsage) setUsage(nextUsage); },
        idle: (nextUsage, activitySeq) => {
          setNotice('');
          // Do not clear ask here. Only snapshot/ask_resolved can say whether the parked question remains.
          const repairing = truncatedPendingRef.current;
          const needsSettlement = settlementDirty.current;
          truncatedPendingRef.current = false;
          settlementDirty.current = false;
          const revision = transcriptEventRevision.current;
          const session = boundSessionRef.current;
          const current = () => revision === transcriptEventRevision.current && session === boundSessionRef.current;
          if (repairing || needsSettlement) {
            const history = repairing ? loadHistory(generation, current) : refreshSettlement(generation, current);
            void history.then(applied => { if (applied && activitySeq !== undefined && current()) markActivityBoundary(session, activitySeq); })
              .catch(() => { if (generation === genRef.current && session === boundSessionRef.current) toast(t.brainChat.historyLoadError, 'error'); });
          } else if (activitySeq !== undefined) markActivityBoundary(session, activitySeq);
          if (nextUsage) setUsage(nextUsage);
          // Reconcile settled control, including a newly submitted plan. Mode changes already arrive
          // through durable chat events. Fence this read so it cannot overwrite newer control or navigation;
          // an absent mode is unknown, never an implicit Build selection.
          const controlStamp = ++hydrationStampRef.current.control;
          const controlSession = boundSessionRef.current;
          void readStatus(controlSession)
            .then((status) => {
              if (generation !== genRef.current || boundSessionRef.current !== controlSession
                || hydrationStampRef.current.control !== controlStamp) return;
              applyControl(status);
            })
            .catch(() => { /* the mode stays as hydrated; the next settle or reconnect reads it again */ });
        },
      },
    });
    const st = await status;
    if (!isCurrentBinding(confirmed)) return null;
    if (!st) return confirmed;
    const fresh = hydrationStampRef.current;
    // A `session` event rebound the stream while this explicit-session status read was in flight; every
    // field in that response belongs to the conversation we already left.
    if (fresh.session !== statusHydrationStamp.session) return confirmed;
    setUsageIfFresh(st.usage, statusUsageRead);
    applyTelemetry(st, fresh.project === statusHydrationStamp.project);
    setLineCfg(st.statusline);
    if (fresh.model === statusHydrationStamp.model && statusModelRead > modelPublishedRef.current) {
      modelPublishedRef.current = statusModelRead;
      setCurrentModel(st.model);
      setFast(st.fast === true);
      applyProviderIdentity(st);
    }
    if (fresh.control === statusHydrationStamp.control) {
      applyControl(st);
    }
    if (fresh.cards === statusHydrationStamp.cards) setCards(withoutBackgroundProcessCards(st.cards ?? []));
    if (fresh.processes === statusHydrationStamp.processes) setProcesses(st.processes ?? []);
    if (fresh.queue === statusHydrationStamp.queue) setQueued(st.queued ?? []);
    if (statusRecalledRevision === recalledRevisionRef.current) restoreRecalled(boundSessionRef.current, st.recalled ?? []);
    return confirmed;
  };

  // Acknowledge only a boundary this live tab rendered. The activity list can refresh independently, so a
  // newer sequence arriving without a matching snapshot/terminal boundary is never cleared by this tab.
  useEffect(() => {
    const boundary = activityBoundaryRef.current;
    if (!boundary || !boundary.refreshed) return;
    if (!conversationReadState || messageWindow || document.hidden || !document.hasFocus() || !attachedRef.current || !ready || reconnecting
      || ![...surfaceReaders.current].some(latestVisible => latestVisible())) return;
    if (boundary.generation !== genRef.current || boundary.sessionId !== boundSessionRef.current) return;
    const seq = boundary.renderedSeq;
    if (seq === null) return;
    const previous = activityAckedRef.current.get(boundary.sessionId) ?? 0;
    const lastSettled = turns.findLast(turn => turn.id && (turn.role === 'you' || turn.role === 'elowen') && turn.settled);
    if (!lastSettled?.id || (lastSettled.role !== 'you' && lastSettled.role !== 'elowen')) return;
    // Activity sequence and the personal message cursor advance independently. A settled live message can
    // still lack a position until terminal history hydration, so its identity admits the native CAS write.
    const newerMessage = lastSettled.branchDepth === undefined
      ? lastSettled.id !== conversationReadState.lastReadMessageId
      : isUnseenPosition(lastSettled.branchDepth, conversationReadState.lastReadDepth);
    if (seq <= previous && !newerMessage) {
      if (activityBoundaryRef.current === boundary) activityBoundaryRef.current = null;
      return;
    }
    if (activityAckInFlightRef.current !== null) return;
    const key = `${boundary.sessionId}:${seq}:${lastSettled.id}:${conversationReadState.revision}`;
    activityAckInFlightRef.current = key;
    void elowenClient.brainReadActivity(boundary.sessionId, { kind: 'messages', through: seq, surface: 'web', messageId: lastSettled.id, revision: conversationReadState.revision })
      .then((result) => {
        if (boundary.generation === genRef.current && boundary.sessionId === boundSessionRef.current) setConversationReadState(current => current && current.revision > result.conversationReadState.revision ? current : result.conversationReadState);
        activityAckedRef.current.set(boundary.sessionId, Math.max(previous, seq));
        if (activityBoundaryRef.current === boundary) activityBoundaryRef.current = null;
      })
      .catch((error: unknown) => {
        if (boundary.generation !== genRef.current || boundary.sessionId !== boundSessionRef.current) return;
        // A rejected revision or an ambiguous write requires a fresh authoritative read, not a blind replay.
        if (activityBoundaryRef.current === boundary) activityBoundaryRef.current = null;
        if (isUnconfirmedWrite(error) || (error instanceof ElowenApiError && error.status === 409)) {
          void elowenClient.brainStatus(boundary.sessionId).then(status => {
            if (boundary.generation === genRef.current && boundary.sessionId === boundSessionRef.current) setConversationReadState(current => current && status.conversationReadState && current.revision > status.conversationReadState.revision ? current : status.conversationReadState);
          }).catch(() => {
            if (boundary.generation === genRef.current && boundary.sessionId === boundSessionRef.current) toast(t.brainChat.statusReadError, 'error');
          });
        } else toast(typedRefusalMessage(error) ?? t.brainChat.statusReadError, 'error');
      })
      .finally(() => {
        if (activityAckInFlightRef.current === key) {
          activityAckInFlightRef.current = null;
          if (activityBoundaryRef.current && activityBoundaryRef.current !== boundary) setActivityWakeVersion(version => version + 1);
        }
      });
  }, [activityBoundaryVersion, activityWakeVersion, pageVisible, sessions.data, ready, reconnecting, hasSurface, messageWindow, conversationReadState, turns, t.brainChat.statusReadError, toast]);

  // Resume only ordinary own conversations. Delegated children always open read-only.
  const openRequest = async (req: BrainOpenRequest): Promise<void> => {
    const view = { sessionId: req.sessionId, conversation: req.delegated ? null : req.sessionId, title: req.title ?? null };
    if (req.delegated) return openDelegatedChild({ ...view, delegated: true }, false, req.name ?? null);
    if (req.continuable) {
      // Navigation has no mutation to bind: a newer selection or resync owns the resulting view.
      await selectSession({ session: req.sessionId });
      return;
    }
    return openDelegatedChild({ ...view, delegated: false }, false);
  };

  const selectSession = async (opts: { session?: string; fresh?: boolean; target?: ProjectExecutionRef }, focusComposer = true): Promise<BrainBinding | null> => {
    setReadOnlyView(null); // leaving any read-only preview
    setChildFocus(null); // leaving any focused child
    setChildFocusName(null);
    const confirmed = await connect(opts);
    if (confirmed) {
      await qc.invalidateQueries({ queryKey: ['brain-sessions'] });
      // Explicit navigation, unlike transport recovery, hands focus back after the switcher closes.
      if (focusComposer && isCurrentBinding(confirmed)) bumpFocus();
    }
    return confirmed;
  };
  const switchSession = async (opts: { session?: string; fresh?: boolean }): Promise<void> => {
    requireCurrentBinding(await selectSession(opts));
  };

  // Conversations holding a send whose answer was lost. The daemon takes no client message id and offers no read that
  // tells whether the inbox took a given text (history text is not a receipt), so the outcome cannot be verified:
  // nothing more is sent into that conversation until the person has seen the card and left it.
  const heldSendsRef = useRef<ReadonlySet<string>>(new Set());
  const setSendHold = (session: string, held: boolean): void => {
    const next = new Set(heldSendsRef.current);
    if (held) next.add(session); else next.delete(session);
    heldSendsRef.current = next;
  };
  // The complete composer intent of a held send; leaving parks it on the existing conversation stash.
  const heldIntentsRef = useRef<ReadonlyMap<string, ComposerIntent>>(new Map());
  /** `holds: false` leaves the report and the hold to a caller that owns them (the dashboard composer). */
  const sendMessage = async (text: string, shown: string | undefined, target = binding(), mentions: readonly (number | 'agent')[] = [], holds = true, composer?: Parameters<typeof elowenClient.brainSend>[5], options?: Parameters<typeof elowenClient.brainSend>[6]) => {
    if (holds && target.session && heldSendsRef.current.has(target.session)) throw new SendHeldError();
    setPromptSuggestion(null);
    setSendNonce((n) => n + 1);
    // Only a fresh run needs a header before its authoritative user echo. Steers keep the running header.
    // An awaited fresh-conversation selection can leave this closure holding the previous session's state.
    // A paused agent runs nothing, so a send into the paused conversation on screen reserves no header,
    // unless it mentions the agent: that one message wakes it.
    const sameSession = target.session === activeSessionId;
    const runs = !agentPausedRef.current || mentions.includes('agent');
    const freshRun = !sameSession || (runs && !busy && !view.thinking && !submittedRun?.awaitingEcho);
    const submitted = freshRun ? { startedAt: new Date().toISOString(), awaitingEcho: true } : null;
    if (submitted) setSubmittedRun(submitted);
    try {
      const title = nameDraft.session === target.session ? nameDraft.name.trim() || undefined : undefined;
      return await elowenClient.brainSend(text, shown, target, title, mentions, composer, options);
    } catch (error) {
      if (submitted) setSubmittedRun(current => current === submitted ? null : current);
      if (holds && target.session && isUnconfirmedWrite(error)) {
        const held = target.session;
        setSendHold(held, true);
        // Leaving verifies nothing. The entire submitted intent moves to the existing stash before the hold ends;
        // newer text, mentions and file references stay in the editor. Uploaded files are never deleted.
        reportFailure(`send:${held}`, error, {
          message: t.brainChat.sendError,
          leave: async () => {
            const intent = heldIntentsRef.current.get(held);
            if (intent !== undefined) {
              const generation = genRef.current;
              const revision = recalledRevisionRef.current;
              if (held !== boundSessionRef.current) throw new Error('Return to the conversation before leaving the send hold.');
              const status = await elowenClient.brainStatus(held);
              if (generation !== genRef.current || held !== boundSessionRef.current || revision !== recalledRevisionRef.current) {
                throw new Error('The conversation changed while refreshing recalled inputs.');
              }
              restoreRecalled(held, status.recalled ?? []);
              draft.park(held, intent);
              draft.settleRecalled(held, intent.recalled, false);
            }
            const intents = new Map(heldIntentsRef.current);
            intents.delete(held);
            heldIntentsRef.current = intents;
            setSendHold(held, false);
          },
        });
      }
      throw error;
    }
  };

  /** Every new-conversation entry shares creation, binding and streaming. */
  const startNewConversation: BrainChatValue['startNewConversation'] = async (opts) => {
    const { message, draft: prefill, target, resume, onCreated } = opts ?? {};
    const text = message?.trim();
    // The shell controller may not have attached yet on the dashboard. Fresh creation IS its first
    // attachment, so mounting the chat afterwards must not resume another conversation.
    attachedRef.current = true;
    const confirmed = requireCurrentBinding(await selectSession(resume ? { session: resume } : { fresh: true, target }));
    const session = confirmed.session;
    if (!session) throw new Error('Conversation creation confirmed no session.');
    if (!resume) onCreated?.(session);
    if (text) await sendMessage(text, text, requireCurrentBinding(confirmed), [], false);
    // Success belongs to this operation, including the dashboard's navigation after it.
    requireCurrentBinding(confirmed);
    if (prefill !== undefined) { setInput(prefill); bumpFocus(); }
    return session;
  };

  const refreshQueue = async (): Promise<void> => {
    const session = boundSessionRef.current;
    const generation = genRef.current;
    const stamp = hydrationStampRef.current.queue;
    if (!session) return;
    const status = await elowenClient.brainStatus(session);
    if (generation === genRef.current && session === boundSessionRef.current && stamp === hydrationStampRef.current.queue) setQueued(status.queued ?? []);
  };
  const onQueueSteer = async (id: string): Promise<void> => {
    const session = boundSessionRef.current;
    const generation = genRef.current;
    if (!session || readOnly || childFocus) return;
    let failed = false;
    let failure: unknown;
    try { await elowenClient.brainQueueSteer(session, id); }
    catch (error) { failed = true; failure = error; }
    if (generation !== genRef.current || session !== boundSessionRef.current) return;
    try { await refreshQueue(); }
    catch (error) { failed = true; failure = error; }
    if (generation !== genRef.current || session !== boundSessionRef.current) return;
    if (failed) toast(typedRefusalMessage(failure) ?? t.brainChat.ux.deliveryUnavailable, 'error');
    else toast(t.brainChat.ux.deliveryChanged, 'ok');
  };
  const reissuePending = useRef(false);
  const unconfirmedReissues = useRef(new Map<string, string>());
  const reissue = async (target: import('../../../src/shared/wireContract').BrainReissueTarget, replacementText?: string): Promise<void> => {
    const session = boundSessionRef.current;
    const generation = genRef.current;
    if (!session || readOnly || childFocus || busy || !membership?.isOwner || reissueTarget !== target || reissuePending.current) return;
    const held = unconfirmedReissues.current.get(session);
    if (held) {
      try {
        const receipt = await elowenClient.brainQueueReceipt(session, held);
        if (receipt.id !== held) throw new Error('Reissue receipt identity mismatch.');
        unconfirmedReissues.current.delete(session);
        if (generation === genRef.current && session === boundSessionRef.current) { setReissueTarget(null); await loadHistory(generation); }
      } catch {
        if (generation === genRef.current && session === boundSessionRef.current) toast(t.brainChat.ux.reissueUnconfirmed, 'error');
      }
      return;
    }
    const requestId = randomId();
    const current = (): boolean => generation === genRef.current && session === boundSessionRef.current;
    reissuePending.current = true;
    let uncertainResponse = false;
    try {
      try {
        const result = await elowenClient.brainRewind(session, target.expectedMessageId, false, { requestId, userMessageId: target.userMessageId, ...(replacementText === undefined ? {} : { replacementText }) });
        if (result.status !== 'reissued' || result.queued !== requestId) {
          uncertainResponse = true;
          throw new Error('Reissue response did not confirm the requested receipt.');
        }
      } catch (error) {
        if (uncertainResponse || isUnconfirmedWrite(error)) {
          // An unknown write must not be issued again under a new id, even after a switch.
          unconfirmedReissues.current.set(session, requestId);
          if (current()) setReissueTarget(null);
          try {
            const receipt = await elowenClient.brainQueueReceipt(session, requestId);
            if (receipt.id !== requestId) throw new Error('Reissue receipt identity mismatch.');
            unconfirmedReissues.current.delete(session);
          } catch {
            const failure = uncertainResponse ? new ElowenApiError('Reissue outcome is unknown.', 503, WRITE_OUTCOME_UNKNOWN) : error;
            reportFailure(`reissue:${session}`, failure, {
              reload: releasing(async () => {
                const receipt = await elowenClient.brainQueueReceipt(session, requestId);
                if (receipt.id !== requestId) throw new Error('Reissue receipt identity mismatch.');
                await invalidateConversationLists(qc);
                if (current()) await loadHistory(generation);
              }, () => unconfirmedReissues.current.delete(session)),
            });
            return;
          }
        } else {
          if (!current()) return;
          if (error instanceof ElowenApiError && error.status === 409) await loadHistory(generation);
          if (current()) toast(typedRefusalMessage(error) ?? t.brainChat.rewindFailed, 'error');
          return;
        }
      }
      await invalidateConversationLists(qc);
      if (current()) {
        setReissueTarget(null);
        await loadHistory(generation);
      }
    } finally {
      reissuePending.current = false;
    }
  };
  const revokeConversationApprovals = async (): Promise<void> => {
    const session = boundSessionRef.current;
    const generation = genRef.current;
    if (!session || readOnly || childFocus) return;
    try {
      await elowenClient.brainRevokeConversationApprovals(session);
      if (generation === genRef.current && session === boundSessionRef.current) toast(t.brainChat.ux.consentsRevoked, 'ok');
    } catch (error) {
      if (generation === genRef.current && session === boundSessionRef.current) toast(typedRefusalMessage(error) ?? t.brainChat.sendError, 'error');
    }
  };
  const beginReply = (messageId: string): void => {
    const sessionId = boundSessionRef.current;
    if (sessionId && !readOnly && !childFocus) draft.setReplyTarget({ id: messageId });
  };
  const cancelReply = (): void => {
    const session = boundSessionRef.current;
    if (session) draft.setReplyTarget(null);
  };
  const restoreRecalledInput = (id: string): void => {
    try { draft.restoreRecalledInput(id); } catch { toast(t.brainChat.sendError, 'error'); }
  };
  const closeMessageWindow = (): void => { windowRequest.current += 1; setMessageWindow(null); };
  const openMessageWindow = async (messageId: string): Promise<void> => {
    const session = boundSessionRef.current;
    if (!session) return;
    const generation = genRef.current;
    const request = ++windowRequest.current;
    const page = await elowenClient.brainMessagesPage(session, { limit: 100, around: messageId });
    if (generation === genRef.current && session === boundSessionRef.current && request === windowRequest.current) setMessageWindow(page);
  };

  const submit = async (delivery?: import('../../../src/shared/wireContract').BrainInputDelivery): Promise<void> => {
    if (readOnly || childFocus) return;
    const input = draft.getSnapshot();
    const typed = input.trim();
    // A message sent mid-turn is STEERED into the running turn via PI's steering queue — the composer
    // stays live. The DAEMON renders every user turn authoritatively (the `user` stream event), so there
    // is NO optimistic local echo — a mid-turn send that queues can't drop or double-render.
    if (!typed && attachments.length === 0) return;
    // A send whose answer was lost is held with its card; the draft waits where it is.
    if (heldSendsRef.current.has(boundSessionRef.current ?? '')) return;
    // A file still uploading belongs with this message: sending now would carry it with the next one.
    if (uploads.length > 0) return;
    // A plugin prompt command (`/review auth…`) rides RAW: the daemon hands the slash to PI, which expands
    // the template's arguments natively — same contract as the CLI. Built-ins/plain text pass through too.
    //
    // An attachment rides as its PATH, not its content. The file is already in the user's project by the
    // time this runs, so the agent opens it with its ordinary file tools — which handle every type,
    // including images, better than inlining ever did.
    const { text, display: shown } = composeAttachmentMessage(typed, attachments, t.brainChat);
    const submittedInput = input;
    const submittedAttachments = attachments;
    // Validate before reserving receipts, so a broken mention cannot strand them in flight.
    const submittedMentions = draft.getMentions();
    const mentions = mentionIdsIn(input, submittedMentions);
    const selectedCommand = draft.commandForDraft(input);
    // Structured expansion consumes args instead of text, so include the same persisted file paths there.
    const command = selectedCommand && attachments.length
      ? { ...selectedCommand, args: composeAttachmentMessage(selectedCommand.args ?? '', attachments, t.brainChat).text }
      : selectedCommand;
    const recalled = draft.takeRecalled();
    const target = binding();
    const reply = target.session ? draft.getReplyTarget() : null;
    draft.setReplyTarget(null);
    setInput('');
    setAttachments([]);
    // No optimistic bubble: the daemon streams a `user` event (which flips busy on + renders the 'you'
    // turn) for both an immediate run and a queued delivery. `shown` rides as the clean display. The
    // binding lands the turn in THIS controller's conversation regardless of the server's active pointer.
    // If the daemon rejects the request, restore this draft unless the user already started a newer one.
    // Attachments are persisted files the agent opens through its tools.
    try {
      await sendMessage(text, shown, target, [...new Set([...mentions, ...recalled.flatMap(row => row.composer ? [] : row.mentions ?? [])])], true, {
        composer: { text: submittedInput, attachments: submittedAttachments, mentions: [...submittedMentions], replyTo: reply },
        recalledInboxIds: recalled.map(row => row.id),
        images: recalled.flatMap(row => row.images ?? []),
      }, { ...(delivery === undefined ? {} : { delivery }), ...(reply ? { replyTo: { id: reply.id } } : {}), ...(command ? { command } : {}) });
      if (target.session) draft.settleRecalled(target.session, recalled, true);
      promptHistory.record(submittedInput);
    }
    catch (error) {
      // A refused message comes back with its mentions, minus the one account that is no longer a member.
      const stale = staleMention(error);
      const kept = stale === undefined ? submittedMentions : submittedMentions.filter((mention) => mention.id !== stale);
      if (isUnconfirmedWrite(error) && target.session) {
        const intent: ComposerIntent = { text: submittedInput, mentions: submittedMentions, attachments: submittedAttachments, recalled, replyTo: reply };
        heldIntentsRef.current = new Map(heldIntentsRef.current).set(target.session, intent);
        draft.showHeld(target.session, intent);
        return;
      }
      if (target.session) {
        draft.restoreSession(target.session, submittedInput, kept, recalled, submittedAttachments, reply);
        draft.settleRecalled(target.session, recalled, false);
      }
      if (target.session !== boundSessionRef.current) {
        toast(t.brainChat.sendErrorPreviousConversation, 'error');
        return;
      }
      if (!target.session) {
        setInput((current) => current || submittedInput);
        setAttachments((current) => [...new Map([...submittedAttachments, ...current].map(file => [file.path, file])).values()]);
      }
      // Show the daemon's own reason: a turn it would
      // not accept says WHY it would not (a conversation whose history could not be saved, a daemon that is
      // shutting down). Only a daemon that gave no reason at all — a dropped connection — falls back to the
      // generic sentence, which is why this reads the code and not `apiErrorMessage`, whose second choice is
      // the transport line ("elowen 500 on /api/brain/send") and names nothing the person can act on. A typed
      // refusal (an offline computer) reads as its localized sentence instead of the daemon's English.
      // A send whose answer was lost is not a daemon reason: the card says so itself (the draft stays editable).
      if (error instanceof SendHeldError) return; // that earlier send's card already says so; the draft is back in the editor
      const reason = stale !== undefined ? t.chat.mentionNotParticipantError
        : error instanceof ElowenApiError && error.code === 'conversation_busy' ? t.brainChat.conversationBusy
          : typedRefusalMessage(error) ?? (error instanceof ElowenApiError ? error.code : undefined);
      toast(reason || t.brainChat.sendError, 'error');
    }
  };

  // Read-only transcript stream. Child identity, usage and cards never borrow the parent cache.
  const openDelegatedChild = async (readOnlyTarget: ReadOnlyView, ownedChild: boolean, name: string | null = null, refused?: () => void): Promise<void> => {
    const { sessionId } = readOnlyTarget;
    stream.close();
    const generation = nextGeneration();
    setAsk(null); setNotice(''); setGoal(null); setPromptSuggestion(null); setProcesses([]);
    // Usage is the child's own too: its step/idle frames (replayed in the snapshot, then live) are the one
    // source, as in the CLI child view. Clearing through the stream writer also fences every parent status
    // read still in flight, so the parent's context window can never land on the child's statusline.
    setUsage(null);
    setChildFocus(ownedChild ? sessionId : null);
    setChildFocusName(name);
    // Hide the composer immediately, including while the child snapshot is loading.
    setControl(null);
    setModelRetry(null);
    setConnectionState(null);
    setConnectionFailure(null);
    setView(emptyView());
    setCards([]);
    setPendingPlan(null);
    setQueued([]);
    setMembership(null);
    setTurnWriter(null);
    setReady(false);
    setTelemetry(EMPTY_TELEMETRY);
    setReadOnlyView(readOnlyTarget);
    setChildDelegation(undefined);
    // The parent identity is wrong for every child-specific control while the snapshot is in flight. Clear
    // it and address the child immediately; the atomic snapshot below fills the authoritative pair.
    setCurrentModel('');
    setFast(false);
    applyProviderIdentity({ icon: '' });
    setActiveSessionId(sessionId);
    clearHistoryWindow();
    await stream.openReadOnly({
      generation,
      session: sessionId,
      handlers: {
        event: (event) => {
          if (event.type === 'delegation') {
            setChildDelegation(event.delegation);
            return;
          }
          applyEvent(event);
        },
        snapshot: (snap) => {
          setChildDelegation(snap.session?.delegation);
          const folded = fromSnapshot(snap);
          replaceNewestWindow(() => ({ ...folded, thinking: snap.control.streaming }), snap.nextBefore ?? null, snap.hasMore ?? false);
          setCards(withoutBackgroundProcessCards(snap.cards ?? []));
          setProcesses(snap.processes ?? []);
          setUsage(snapshotUsage(snap.events) ?? null);
          if (snap.session) {
            setCurrentModel(snap.session.model); applyProviderIdentity(snap.session); setFast(snap.session.fast);
            // A read-only view still names who is in the conversation; it changes nothing.
            setMembership(membershipOf(snap.session));
            setTelemetry({ ...EMPTY_TELEMETRY, projectRef: snap.session.projectRef ?? null, project: snap.session.project ?? null });
          }
          if (snap.sessionId) setActiveSessionId(snap.sessionId);
          applyControl(snap.control);
          if (Object.prototype.hasOwnProperty.call(snap, 'goal')) setGoal(snap.goal ?? null);
          setPromptSuggestion(snap.promptSuggestion ?? null);
          truncatedPendingRef.current = snap.control.streaming === true && snap.truncated === true;
          if (snap.truncated && !snap.control.streaming) {
            void loadHistory(generation).catch(() => toast(t.brainChat.historyLoadError, 'error'));
          }
        },
        ask: ({ id, questions, kind }) => setAsk({ id, questions, kind }),
        askResolved: (id) => setAsk((current) => current?.id === id ? null : current),
        goal: setGoal,
        promptSuggestion: setPromptSuggestion,
        queue: () => { void refreshQueue().catch(error => toast(typedRefusalMessage(error) ?? t.brainChat.queueRemoveError, 'error')); },
        step: (nextUsage) => { if (nextUsage) setUsage(nextUsage); },
        idle: (nextUsage) => {
          if (nextUsage) setUsage(nextUsage);
          if (truncatedPendingRef.current) {
            truncatedPendingRef.current = false;
            void loadHistory(generation).catch(() => toast(t.brainChat.historyLoadError, 'error'));
          }
        },
        card: (card) => {
          if (isBackgroundProcessCardId(card.id)) return;
          setCards((cur) => upsertCard(cur, card));
        },
        process: setProcesses,
        error: () => {
          if (truncatedPendingRef.current) {
            truncatedPendingRef.current = false;
            void loadHistory(generation).catch(() => toast(t.brainChat.historyLoadError, 'error'));
          }
        },
        openError: () => {
          if (refused) { refused(); return; }
          toast(t.brainChat.searchOpenError, 'error');
          setReady(false);
        },
      },
    });
  };

  /** All descendants open read-only. Owned focus only preserves targeted controls and the name. A
   *  descendant runs in the conversation of the view it was opened from: the live conversation, or the
   *  conversation an open read-only view already belongs to. */
  const focusSubagentSession = (sessionId: string, name: string | null = null): Promise<void> =>
    openDelegatedChild({
      sessionId, delegated: true,
      conversation: readOnlyView ? readOnlyView.conversation : activeSessionId,
      title: readOnlyView?.title ?? null,
    }, !readOnly || childFocus !== null, name);

  // Leave the read-only preview / the focused child and return to the live active conversation.
  const exitReadOnly = (): void => { setReadOnlyView(null); setChildFocus(null); setChildFocusName(null); void connect(); };

  const deleteSession = async (id: string): Promise<void> => {
    await elowenClient.brainDeleteSession(id);
    draft.forgetSession(id);
    await qc.invalidateQueries({ queryKey: ['brain-sessions'] });
    // A deleted conversation takes both its collapsed branches with it: the schedules stay but the daemon
    // no longer resolves their filing, and its delegated children are deleted with it. Either way the
    // navigation read has to be asked again rather than patched.
    void qc.invalidateQueries({ queryKey: QUERY_KEYS.brainConversationLinks });
    // Deleting the open conversation re-targets to the most recent remaining one (or a fresh state).
    if (id === activeSessionRef.current) await connect();
  };

  const reactToMessage: BrainChatValue['reactToMessage'] = async (session, messageId, emoji, add) => {
    if (readOnly || childFocus || session !== boundSessionRef.current) throw new Error('The message reacted to is no longer on screen');
    return add ? elowenClient.brainAddReaction(session, messageId, emoji) : elowenClient.brainRemoveReaction(session, messageId, emoji);
  };

  const addFiles = async (files: Iterable<File>): Promise<void> => {
    const session = boundSessionRef.current;
    // Uploaded on attach rather than on send, so a large file is already on disk by the time the user
    // finishes typing instead of stalling the send, and a failure is reported while they can still act.
    // Every file shows up at once as a pending upload; they go up one after another.
    const queue = [...files].map((file) => ({ file, key: ++uploadKeyRef.current, controller: new AbortController() }));
    if (!queue.length) return;
    for (const { key, controller } of queue) uploadControllers.current.set(key, controller);
    setUploads((cur) => [...cur, ...queue.map(({ file, key }) => ({ key, name: file.name || 'upload', size: file.size, sent: 0 }))]);
    for (const { file, key, controller } of queue) {
      // The conversation decides where the file lands, so the upload carries its id — opaque, and
      // re-authorized server-side against this account before anything is written.
      const a: Attachment | AttachRefusal = controller.signal.aborted
        ? { failed: true, cancelled: true }
        : await uploadAttachment(file, session, {
          signal: controller.signal,
          onProgress: (sent) => setUploads((cur) => cur.map((u) => (u.key === key ? { ...u, sent } : u))),
        }).catch((): AttachRefusal => ({ failed: true }));
      uploadControllers.current.delete(key);
      setUploads((cur) => cur.filter((u) => u.key !== key));
      if (controller.signal.aborted) continue;
      // The server's own words when it sent any: "ask an administrator to assign you one" is the half a
      // user can act on, and the generic copy is what kept a one-line configuration fault unexplained.
      if ('failed' in a) { if (!a.cancelled) toast(a.reason ?? t.brainChat.attachFailed, 'error'); continue; }
      setAttachments((cur) => [...cur, a]);
    }
  };
  const cancelUpload = (key: number): void => uploadControllers.current.get(key)?.abort();
  const removeAttachment = (index: number): void => setAttachments((cur) => cur.filter((_, j) => j !== index));

  // Optimistic remove: the item disappears immediately, but on failure it is still queued server-side, so
  // it comes back rather than leaving the UI claiming it was removed. Hiding it (instead of splicing the
  // list) is what makes the rollback exact — it restores the item's own position, never a remembered one.
  const onQueueRemove = (id: string): void => {
    if (removingQueue.has(id) || !queued.some((x) => x.id === id)) return;
    const generation = genRef.current;
    const session = boundSessionRef.current;
    setRemovingQueue((cur) => new Set(cur).add(id));
    void elowenClient.brainQueueRemove(id, session).catch(() => {
      // Another conversation is on screen (a switch bumped the generation, or a `session` event rebound the
      // session without one): unhiding would plant a ghost chip pointing at a stranger's message, and the
      // error concerns a queue the user is no longer looking at.
      if (generation !== genRef.current || session !== boundSessionRef.current) return;
      // A newer snapshot cleared the set meanwhile: it is authoritative and already carries this item if the
      // server still holds it, so the delete below is a no-op and only the failure is reported.
      setRemovingQueue((cur) => { const next = new Set(cur); return next.delete(id) ? next : cur; });
      toast(t.brainChat.queueRemoveError, 'error');
    });
  };
  // Awaited by AskQuestionCard: the question is only removed from the UI once the server actually
  // accepted the answer. On failure the agent is STILL waiting server-side, so the question must stay
  // on screen (and the card's form re-enable) — losing it here would leave no way to answer without a
  // reconnect or reload.
  const onAnswer = async (id: string, answers: AskAnswer[]): Promise<void> => {
    if (readOnlyView || childFocus !== null) throw new Error('This conversation is read-only.');
    const response = await elowenClient.brainAnswer(id, answers);
    if (!response.matched) {
      const error = new Error(t.brainChat.askExpired);
      error.name = 'QuestionExpired';
      throw error;
    }
    setAsk((cur) => (cur?.id === id ? null : cur));
  };
  const reloadAsk = async (): Promise<void> => {
    const session = boundSessionRef.current;
    const generation = genRef.current;
    const stamp = ++hydrationStampRef.current.control;
    const status = await readStatus(session);
    if (generation === genRef.current && session === boundSessionRef.current
      && stamp === hydrationStampRef.current.control) applyControl(status);
  };
  // Explicit Stop intent — for the FOCUSED delegated child it stops THAT child (the daemon resolves it
  // through the durable ancestry; the hidden parent keeps running), otherwise the bound conversation.
  const abort = (): void => {
    if ((readOnly && !childFocus) || !canStop) return;
    void elowenClient.brainAbort(childFocus ?? boundSessionRef.current).catch((error) => toast(apiErrorMessage(error) || t.brainChat.abortError, 'error'));
  };
  const onQueueRecall = (all = false): void => {
    const session = boundSessionRef.current;
    if (!session || readOnly || childFocus) return;
    void elowenClient.brainQueueRecall(session, all).then(rows => restoreRecalled(session, rows))
      .catch(error => toast(apiErrorMessage(error) || t.brainChat.queueRemoveError, 'error'));
  };
  const rewindPending = useRef(false);
  const rewind = (): void => {
    const session = boundSessionRef.current;
    if (!session || rewindPending.current || readOnly || childFocus) return;
    const head = [...turns].reverse().find((turn) => turn.role === 'you' || turn.role === 'elowen');
    const generation = genRef.current;
    rewindPending.current = true;
    void (async () => {
      // A just-settled SSE reply lacks its durable row id until history is hydrated.
      const page = !busy && !head?.id ? await elowenClient.brainMessagesPage(session, { limit: 100 }) : null;
      if (generation !== genRef.current || session !== boundSessionRef.current) return null;
      const latest = page?.items.findLast((message) => message.role === 'user' || message.role === 'assistant');
      return elowenClient.brainRewind(session, head?.id ?? latest?.id ?? '', busy);
    })()
      .then(async (result) => {
        if (!result) return;
        if (generation !== genRef.current || session !== boundSessionRef.current) return;
        // The rewritten history speaks for a plain removal. An empty history and a removal after tool calls
        // have no visible trace: the second one is a safety warning, since performed actions stay in effect.
        if (result.status === 'empty') toast(t.brainChat.rewindEmpty);
        else if (result.status === 'removed') {
          await loadHistory(generation);
          if (result.hasToolCalls) toast(t.brainChat.rewindActionsRemain);
        }
      })
      .catch(async (error: unknown) => {
        if (generation !== genRef.current || session !== boundSessionRef.current) return;
        if (error instanceof ElowenApiError && error.status === 409) await loadHistory(generation).catch(() => undefined);
        toast(typedRefusalMessage(error) ?? t.brainChat.rewindFailed, 'error');
      })
      .finally(() => { rewindPending.current = false; });
  };

  // The status read supplies viewer-specific queue capabilities; broadcast frames never grant them.
  const visibleQueue = useMemo(() => queued.filter((x) => !removingQueue.has(x.id)), [queued, removingQueue]);

  // Both projections are pure folds of the transcript, so they survive a reconnect for free: history
  // carries the same `sub`/`wf` attachments the live events wrote. Their IDENTITY is held across a token
  // that changed neither, so reading them does not subscribe a consumer to the stream.
  const subagents = useStableList(useMemo(() => collectSubagents(turns), [turns]));
  const workflows = useStableList(useMemo(() => collectWorkflows(turns), [turns]));
  // The same fold, for a plugin surface that covers the transcript. Memoized here rather than in each
  // surface so a streaming reply recomputes ONE bounded string per view change, however many chat
  // surfaces (dock + /chat on a phone) are mounted on this one provider.
  const narration = useMemo(() => liveNarration(turns.slice(0, turns.length - (view.conversationAppendCount ?? 0))), [turns, view.conversationAppendCount]);

  // Fetch the model catalog once for either entry point (header picker / `/model` slash). Never throws:
  // an empty catalog is the RBAC "no allowed model" state, a rejection is the provider-error state.
  const loadModels = async (): Promise<void> => {
    setModelsError(false);
    setModelsLoading(true);
    // The catalog carries the account's image models too; a conversation can never run on one.
    try { setModels(chatModelCatalog(await elowenClient.brainModels())); }
    catch { setModels(null); setModelsError(true); }
    finally { setModelsLoading(false); }
  };
  // Switch this conversation's model IN PLACE: the daemon respawns under the same id and records a
  // `model` chat event that reconciles every attached client — so no attachment/reader teardown here.
  // The initiator updates its own model label immediately (covers an empty conversation that emits no
  // marker). The bound session is unchanged, so the SSE stream stays open on the same generation.
  // The composer draft is left untouched: this runs from the header/dock picker too, where the user may
  // have unsent text typed — only the slash entry clears the input (when it opens the overlay).
  // Closing the model picker lives here rather than only in its own row handler, so a switch started
  // from any other entry point still leaves no stale picker standing over the conversation.
  const runModel = (m: BrainModelOption): Promise<boolean> => {
    setModelOpen(false);
    if (modelHeldRef.current || (readOnly && !childFocus)) return Promise.resolve(false);
    const generation = genRef.current;
    latestModelRef.current = m;
    const attempt: ModelSwitchAttempt = {
      selection: m, session: boundSessionRef.current, target: childFocus ?? boundSessionRef.current, started: false, outcome: null,
      previousEvents: new Set(turns.flatMap((turn) => turn.role === 'event' ? turn.events.map((item) => item.event.id) : [])),
    };
    modelAttemptRef.current = attempt;
    dismissKey(`model-switch:${attempt.session}`);
    setModelQueued(false);
    setModelStatus('saving');
    // Keep HTTP requests in order too; the daemon independently supersedes older queued picks from
    // other clients when their turn lock is released.
    const switchResult = modelQueueRef.current.catch(() => undefined).then(async (): Promise<boolean> => {
      if (generation !== genRef.current || modelAttemptRef.current !== attempt || boundSessionRef.current !== attempt.session) return false;
      for (const id of seenModelEventsRef.current) attempt.previousEvents.add(id);
      attempt.started = true;
      try {
        // Focused delegated child: the SAME picker switch, but the child's own row is what changes —
        // the server persists the pick there and the child's next turn comes up on it. A refusal
        // (turn in flight, foreign session) surfaces with its precise reason.
        const result = childFocus
          ? await elowenClient.brainSubagentSetModel({ provider: m.provider, model: m.model }, attempt.target!)
          : await elowenClient.brainSetModel({ provider: m.provider, model: m.model }, attempt.session);
        if (modelAttemptRef.current !== attempt || boundSessionRef.current !== attempt.session) return true;
        if (attempt.outcome) return attempt.outcome === 'saved';
        if ('queued' in result && result.queued) {
          setModelQueued(true);
          setModelStatus('idle');
          // One authoritative read also covers completion during a dropped stream, without polling.
          void readStatus(attempt.session).then((status) => {
            if (boundSessionRef.current !== attempt.session || modelAttemptRef.current !== attempt || attempt.outcome) return;
            if (status.model !== m.model || status.provider !== m.provider) return;
            settleModelAttempt(attempt, 'saved');
            hydrationStampRef.current.model += 1;
            setCurrentModel(status.model);
            setFast(status.fast === true);
            applyProviderIdentity(status);
          }).catch(() => { /* the model event or reconnect snapshot remains the completion signal */ });
          return true;
        }
        const { model } = result;
        hydrationStampRef.current.model += 1;
        setCurrentModel(model);
        if ('identity' in result) {
          applyProviderIdentity(result.identity);
          setFast(result.identity.fast);
        } else {
          applyProviderIdentity({ provider: m.provider, providerLabel: m.providerLabel });
        }
        settleModelAttempt(attempt, 'saved');
        return true;
      } catch (e) {
        if (modelAttemptRef.current === attempt && boundSessionRef.current === attempt.session && !attempt.outcome) {
          if (holdsAnswer(e)) {
            // The switch may have been applied: hold every other pick until the daemon's own model is read back.
            modelHeldRef.current = attempt;
            setModelQueued(false);
            setModelStatus('unconfirmed');
            reportFailure(`model-switch:${attempt.session}`, e, {
              message: t.common.actionFailed,
              reload: releasing(async () => {
                // The row that was written is the row that is read: the focused child's, or the conversation's.
                const status = await elowenClient.brainStatus(attempt.target);
                if (modelAttemptRef.current !== attempt || boundSessionRef.current !== attempt.session || attempt.outcome) return;
                // What the daemon says is the truth of that row, matching the pick or not; it is only shown while
                // that row is the one on screen.
                if (childFocusRef.current === (attempt.target === attempt.session ? null : attempt.target ?? null)) {
                  hydrationStampRef.current.model += 1;
                  setCurrentModel(status.model);
                  setFast(status.fast === true);
                  applyProviderIdentity(status);
                }
                if (status.model === m.model && status.provider === m.provider) settleModelAttempt(attempt, 'saved');
              }, () => {
                if (modelHeldRef.current !== attempt) return;
                modelHeldRef.current = null;
                setModelStatus('idle');
              }),
            });
            return false;
          }
          // Only a refusal the daemon typed (a code the client knows how to word) is shown as such; any other
          // failure gets the generic sentence instead of transport or free server prose.
          settleModelAttempt(attempt, 'error', typedRefusalMessage(e) ?? t.common.actionFailed);
        }
        return false;
      }
    });
    modelQueueRef.current = switchResult;
    return switchResult;
  };
  const retryModel = (): void => { if (latestModelRef.current) runModel(latestModelRef.current); };
  // Select the daemon's persistent conversation mode. The local field only renders acknowledged control.
  // A focused delegated child is read-only; refuse a switch without changing the bound parent.
  const runMode = (mode: BrainWorkMode): Promise<void> => {
    if (childFocus) { toast(t.brainChat.childFocusCommandDisabled, 'error'); return Promise.resolve(); }
    const sessionId = boundSessionRef.current;
    const generation = genRef.current;
    const stamp = ++hydrationStampRef.current.control;
    setModeSwitching(true);
    const operation = elowenClient.brainSwitchWorkMode(mode, binding()).then(control => {
      if (control.queued || generation !== genRef.current || sessionId !== boundSessionRef.current
        || stamp !== hydrationStampRef.current.control) return;
      applyControl(control);
    }).finally(() => {
      if (modeSwitchRef.current === operation) { modeSwitchRef.current = null; setModeSwitching(false); }
    });
    modeSwitchRef.current = operation;
    return operation;
  };
  // The reasoning picker is refused while a delegated child is focused: it writes the ACTIVE
  // conversation's level AND the account default (POST /brain/think), and a child session is neither —
  // the daemon would 409 with daemon-ese after the modal was already open. Say it up front instead.
  const openReasoning = (v: boolean): void => {
    if (v && childFocus) { toast(t.brainChat.childFocusCommandDisabled, 'error'); return; }
    // The reasoning level is the owner's, like the model.
    if (v && guest) { toast(t.chat.ownerOnlyControl, 'error'); return; }
    setReasoningOpen(v);
  };
  // The daemon alone owns pending/decided state, shared by every surface.
  const planDecision = workMode === 'plan' && pendingPlan && !busy && !readOnly && !childFocus
    ? pendingPlan : null;
  const [planSubmitting, setPlanSubmitting] = useState(false);
  const planInFlightRef = useRef(false);
  const decidePlan = (decision: 'implement' | 'dismiss'): void => {
    if (!planDecision || !activeSessionId || readOnlyView || childFocus || planInFlightRef.current) return;
    const sessionId = activeSessionId;
    const controlStamp = hydrationStampRef.current.control;
    planInFlightRef.current = true;
    setPlanSubmitting(true);
    void elowenClient.brainPlanDecision(sessionId, planDecision.id, decision)
      .then(({ control }) => {
        if (sessionId !== activeSessionRef.current) return;
        // Accepted implementation changes the composer even if its own mode event overtook this response.
        if (controlStamp !== hydrationStampRef.current.control) return;
        applyControl(control);
      })
      .catch(async (error: unknown) => {
        // Implementing starts a turn, so a typed turn refusal (an offline computer) is a reason to show,
        // not a stale plan to re-read.
        const refusal = typedRefusalMessage(error);
        if (refusal) { toast(refusal, 'error'); return; }
        if (!(error instanceof ElowenApiError) || error.status !== 409) {
          toast(t.brainChat.planDecisionError, 'error'); return;
        }
        const fresh = await readStatus(sessionId);
        if (sessionId !== activeSessionRef.current || controlStamp !== hydrationStampRef.current.control) return;
        hydrationStampRef.current.control += 1;
        if (fresh.workMode) { setControlMode(fresh.workMode); }
        setPendingPlan(fresh.pendingPlan ?? null);
      })
      .catch(() => toast(t.brainChat.planDecisionError, 'error'))
      .finally(() => { planInFlightRef.current = false; setPlanSubmitting(false); });
  };
  const dismissPlan = (): void => decidePlan('dismiss');
  const implementPlan = (): void => decidePlan('implement');
  const renameSession = async (title: string): Promise<void> => {
    const next = title.trim();
    const id = boundSessionRef.current ?? activeSessionId;
    if (!next || !id) return;
    try {
      await renameConversation(qc, id, next);
      setRenameOpen(false);
    } catch (error) {
      toast(t.chat.renameError, 'error');
      throw error;
    }
  };
  // Structured selections survive colliding or long names; only the daemon resolves the live skill.
  const sendPluginPicker = (input: string | import('../../../src/shared/wireContract').CommandInvocation): void => {
    if (readOnly || childFocus) return;
    const target = binding();
    const command = typeof input === 'string' ? undefined : input;
    const text = typeof input === 'string' ? input : `/${input.name}${input.args ? ` ${input.args}` : ''}`;
    void sendMessage(text, undefined, target, [], true, undefined, command ? { command } : undefined)
      .catch((error) => { if (!isUnconfirmedWrite(error) && !(error instanceof SendHeldError)) toast(typedRefusalMessage(error) ?? t.brainChat.sendError, 'error'); });
  };
  const runSlash = async (cmd: SlashCommandDef, argument?: string, options?: RunSlashOptions): Promise<void> => {
    // Only a command read out of the composer text consumes the field; a button or menu leaves the draft alone.
    const clearComposer = (): void => { if (options?.fromComposer) setInput(''); };
    // These open an owner-only control in place; the daemon itself refuses a guest's routed commands.
    if (guest && (cmd.kind === 'mode' || cmd.name === 'model' || cmd.name === 'rename')) {
      toast(t.chat.ownerOnlyControl, 'error');
      return;
    }
    if (cmd.name === 'model') { clearComposer(); setModelOpen(true); void loadModels(); return; }
    // A held send's text waits in the editor; a macro that would send is refused before it clears the field.
    if (cmd.kind === 'prompt' && cmd.invocation && heldSendsRef.current.has(boundSessionRef.current ?? '')) return;
    clearComposer();
    // Focused delegated child: the viewed composer's slash set shrinks to what a child actually
    // supports. Only navigation, help and targeted stop remain available;
    // the rest is parent-scoped (mode/reasoning/rename/actions/pickers) and is refused with the
    // reason here — never executed against the hidden parent behind the view. `/tasks` is refused too,
    // exactly as the CLI child view refuses its task actions: the child's list is SHOWN from its card,
    // but the todo routes act only on the viewer's own conversations.
    const childCapable = ['new', 'help', 'stop'].includes(cmd.name);
    if (childFocus && !childCapable) { toast(t.brainChat.childFocusCommandDisabled, 'error'); return; }
    try {
      if (cmd.name === 'new') { await startNewConversation(); return; }
      if (cmd.name === 'help') { openHelp(); return; }
      if (cmd.name === 'stats') {
        setStatsOpen(true);
        // Refresh usage data for the modal — fenced, so opening it mid-turn cannot roll the statusline
        // back to a figure the stream has already moved past.
        void refreshUsage().catch(() => { /* best-effort; stream usage stays authoritative */ });
        return;
      }
      if (cmd.name === 'reasoning') { openReasoning(true); return; }
      if (cmd.kind === 'picker' && (cmd.plugin || cmd.requiresPlugin)) {
        setPluginPicker({
          plugin: cmd.plugin ?? cmd.requiresPlugin!,
          command: cmd.name,
          // The same rule as `pluginSessionId`: a picker opened over a focused child (only `/skills`,
          // which sends a turn instead of calling a route) gets no conversation to act on.
          sessionId: childFocus ? null : boundSessionRef.current ?? null,
          ...(argument === undefined ? {} : { argument }),
        });
        return;
      }
      if (cmd.kind === 'mode') {
        const mode = WORK_MODES.find((m) => m === cmd.name);
        if (mode) { await runMode(mode); return; }
      }
      if (cmd.name === 'rename') { setRenameOpen(true); return; }
      // A prompt macro usually wants arguments — picking it pre-fills the composer (`/review `) so the
      // user types them and submits; the submit path expands the template (args or not).
      if (cmd.kind === 'prompt') {
        setInput(`/${cmd.name}${argument ? ` ${argument}` : ' '}`);
        return;
      }
      if (cmd.kind === 'action') {
        // Only /stop reaches here while a child is focused (the gate above refused the rest) — and it
        // means the VIEWED child's abort, the same intent as the header's Stop button.
        if (childFocus) { abort(); return; }
        const r = await elowenClient.brainCommand(cmd.name, boundSessionRef.current, argument);
        if (r.data && Object.prototype.hasOwnProperty.call(r.data, 'goal')) setGoal(r.data.goal ?? null);
        // A command the daemon recorded in the conversation shows as its row there; only the rest
        // (`/stop`, `/clear`) confirm with a toast.
        if (!r.recorded) toast(r.message ?? `/${cmd.name}`, 'ok');
        return;
      }
      toast(`/${cmd.name}`, 'error');
    } catch (error) {
      if (cmd.kind === 'prompt' && (isUnconfirmedWrite(error) || error instanceof SendHeldError)) return; // reported and held by sendMessage
      toast(typedRefusalMessage(error) ?? (cmd.kind === 'mode' ? t.brainChat.modeSwitchError : t.brainChat.sendError), 'error');
    }
  };

  // --- Lazy attach + the cross-mount bridge (session/composer requests + live BRAIN_* events). ---
  const ensureAttached = (): boolean => {
    if (attachedRef.current) return false;
    attachedRef.current = true;
    // If another view asked to open a specific session (Sessions → open in chat), open THAT one instead of
    // the default active conversation; otherwise boot the active conversation as usual. A pending composer
    // draft (suggestion/launcher) seeds the input + focuses.
    const pending = consumePendingBrainSession();
    const pendingText = consumePendingBrainComposer();
    if (pendingText !== null) { setInput(pendingText); requestAnimationFrame(bumpFocus); }
    const issuedIn = authGeneration();
    const boot = pending ? openRequest(pending) : connect();
    const generation = genRef.current;
    void boot.catch((error) => {
      if (issuedIn !== authGeneration() || generation !== genRef.current) return;
      toast(apiErrorMessage(error) || t.brainChat.historyLoadError, 'error');
    });
    return true;
  };

  const openConversationLink = (sessionId: string): void => {
    attachedRef.current = true;
    const issuedIn = authGeneration();
    // The link's surface owns focus, including its named-message jump. A late list refresh must not
    // hand it back to the composer as an ordinary conversation-switcher selection would.
    const selection = selectSession({ session: sessionId }, false);
    const generation = genRef.current;
    const current = (): boolean => issuedIn === authGeneration() && generation === genRef.current;
    void (async () => {
      try {
        requireCurrentBinding(await selection);
        return;
      } catch (error) {
        // Only the daemon's refusal of a writer reads on; a newer selection or any other failure ends here.
        if (!current() || !(error instanceof ElowenApiError) || (error.status !== 404 && error.status !== 403)) throw error;
      }
      await openDelegatedChild({ sessionId, delegated: false, conversation: sessionId, title: null }, false, null, () => {
        toast(t.chat.conversationUnavailable, 'error');
        exitReadOnly();
      });
    })().catch((error) => {
      if (!current()) return;
      toast(apiErrorMessage(error) || t.brainChat.historyLoadError, 'error');
    });
  };

  const onOpenRef = useRef<(req: BrainOpenRequest | undefined) => void>(() => {});
  onOpenRef.current = (req) => {
    if (req?.displayOnly) return;
    // If this is the first open (nothing mounted yet), ensureAttached boots WITH the pending request.
    if (ensureAttached()) return;
    consumePendingBrainSession(); // this controller handles it live → clear the pending bridge
    if (req?.sessionId) void openRequest(req).catch((error) => toast(typedRefusalMessage(error) ?? t.brainChat.searchOpenError, 'error'));
  };
  const onComposeRef = useRef<(text: string | undefined) => void>(() => {});
  onComposeRef.current = (detailText) => {
    if (ensureAttached()) return; // first open boots with the pending composer draft + focus
    const bridged = consumePendingBrainComposer();
    const requestedText = bridged ?? detailText ?? '';
    // An empty launcher request means "focus". A non-empty suggestion is appended to an existing
    // draft so opening the shared composer can never silently destroy unsent text.
    if (requestedText) setInput((current) => mergeBrainComposerText(current, requestedText));
    if (readOnly || childFocus) {
      // A read-only preview or a focused child has closed the bound conversation's reader and
      // replaced the view. Reconnect before showing the composer so the seeded draft and the stream
      // target the same (parent) conversation.
      setReadOnlyView(null);
      setChildFocus(null);
      setChildFocusName(null);
      void connect().then(bumpFocus).catch((error) => {
        toast(apiErrorMessage(error) || t.brainChat.historyLoadError, 'error');
        bumpFocus();
      });
    } else {
      bumpFocus();
    }
  };

  // Losing the conversation on screen (removed by its owner, left, or deleted) moves this viewer to its own
  // active conversation at once: the daemon has already detached the stream, so waiting for a rebind would
  // leave a dead view. A child of that conversation goes with it; any other conversation stays put.
  const onRemovedRef = useRef<(removal: ConversationRemoval) => void>(() => {});
  onRemovedRef.current = ({ sessionId }) => {
    const shown = readOnlyView ? readOnlyView.conversation : boundSessionRef.current;
    if (sessionId !== shown) return;
    draft.forgetSession(sessionId);
    toast(t.chat.removedFromConversation);
    setReadOnlyView(null);
    setChildFocus(null);
    setChildFocusName(null);
    void connect().catch((error) => toast(apiErrorMessage(error) || t.brainChat.historyLoadError, 'error'));
  };
  useEffect(() => subscribeConversationRemovals((removal) => onRemovedRef.current(removal)), []);

  useBrainChatLifecycle({
    onOpenRef, onComposeRef, attachedRef, clientId,
    setActivityWakeVersion, stream, silence, silenceRef,
  });

  // ── THE THREE CONTEXT VALUES ───────────────────────────────────────────────────────────────────────
  const dockArchiveHeld = useRef(new Set<string>());
  const dockArchivePending = useRef(new Set<string>());
  const shownDockId = readOnlyView?.conversation ?? activeSessionId;
  const shownDockRef = useRef(shownDockId);
  shownDockRef.current = shownDockId;
  const closeDock = async (): Promise<boolean> => {
    const id = shownDockRef.current;
    if (!id) return true;
    const generation = genRef.current;
    const account = authGeneration();
    const current = () => generation === genRef.current && account === authGeneration() && shownDockRef.current === id;
    let rows = sessions.data;
    if (!rows) {
      try { rows = await qc.ensureQueryData({ queryKey: ['brain-sessions'], queryFn: () => elowenClient.brainSessions('all') }); }
      catch { if (current()) toast(t.brainChat.archiveFailed, 'error'); return false; }
      if (!current()) return false;
    }
    if (!rows) { if (current()) toast(t.brainChat.archiveFailed, 'error'); return false; }
    let row = rows.find(item => item.id === id);
    if (!row && !readOnlyView) {
      // A newly admitted conversation can be newer than the cached list; fetch the authoritative list.
      try {
        await qc.cancelQueries({ queryKey: ['brain-sessions'], exact: true });
        if (!current()) return false;
        rows = await qc.fetchQuery({ queryKey: ['brain-sessions'], queryFn: () => elowenClient.brainSessions('all'), staleTime: 0 });
      } catch { if (current()) toast(t.brainChat.archiveFailed, 'error'); return false; }
      if (!current()) return false;
      if (!rows) { toast(t.brainChat.archiveFailed, 'error'); return false; }
      row = rows.find(item => item.id === id);
      if (!row) { toast(t.brainChat.archiveFailed, 'error'); return false; }
    }
    if (!row || row.dockCloseBehavior !== 'archive' || !row.capabilities.archive || row.archivedAt !== null) return current();
    const key = `${account}:${id}`;
    if (dockArchiveHeld.current.has(key) || dockArchivePending.current.has(key)) return false;
    dockArchivePending.current.add(key);
    try {
      const result = await setConversationArchive(qc, id, { archived: true, revision: row.archiveRevision, when: 'settled' });
      if (!current()) return false;
      if (result.outcome === 'waiting') { toast(t.brainChat.archiveWaiting, 'error'); return false; }
      if (result.outcome === 'archived' || result.outcome === 'deferred') return true;
      throw new Error('Unexpected archive outcome for dock close.');
    } catch (error) {
      if (account !== authGeneration()) return false;
      if (holdsAnswer(error)) dockArchiveHeld.current.add(key);
      const release = () => { dockArchiveHeld.current.delete(key); };
      const failure = error instanceof SaveConflictError ? new SaveConflictError(error.recovery, t.brainChat.archiveConflict) : error;
      reportFailure(`dock-archive:${id}`, releasedBy(failure, release), {
        message: t.brainChat.archiveFailed,
        reload: releasing(async () => {
          if (account !== authGeneration()) throw new Error('Account changed during archive recovery');
          await qc.fetchQuery({ queryKey: ['brain-sessions'], queryFn: () => elowenClient.brainSessions('all'), staleTime: 0 });
          if (account !== authGeneration()) throw new Error('Account changed during archive recovery');
        }, release),
      });
      return false;
    } finally { dockArchivePending.current.delete(key); }
  };

  // Every handler the controller exposes, behind ONE identity apiece. Written as a single literal so the
  // action set is visible in one place and cannot drift from the interface.
  const actionHandlers = useStableActions({
    registerSurface, closeDock,
    notifyActivityVisibility: () => setActivityWakeVersion(version => version + 1),
    setStatsOpen, setReasoningOpen: openReasoning,
    openHelp, closeHelp, setModelOpen,
    sendPluginPicker,
    closePluginPicker: () => setPluginPicker(null),
    crashPluginPicker: setCrashedPicker,
    setInput, setConversationName, addFiles, removeAttachment, cancelUpload, submit, switchSession,
    beginReply, cancelReply, restoreRecalledInput, reportTyping: typingEmitter.onDraftInput, openMessageWindow, closeMessageWindow, onQueueSteer, reissue, revokeConversationApprovals,
    acceptPromptSuggestion: () => {
      if (promptSuggestion && draft.getSnapshot() === '') { setInput(promptSuggestion); setPromptSuggestion(null); }
    },
    focusSubagentSession, exitReadOnly, deleteSession, onQueueRemove, onQueueRecall, onAnswer, reloadAsk, rereadProjectExecution,
    abort, rewind, ensureAttached, openConversationLink, loadOlder, reactToMessage,
    retryConnection: () => stream.retry(),
    setModel: (m: BrainModelOption) => runModel(m),
    loadModels: () => void loadModels(),
    retryModel,
    setShowThoughts: (v: boolean) => setThoughts(v ? 'show' : 'hide'),
    implementPlan, dismissPlan,
    switchWorkMode: (mode: BrainWorkMode) => { if (!readOnly && !childFocus && workMode && !modeSwitching) void runMode(mode).catch(error => toast(apiErrorMessage(error), 'error')); },
    openRename: () => setRenameOpen(true),
    closeRename: () => setRenameOpen(false),
    renameSession,
    openHistory: () => setHistoryOpen(true),
    closeHistory: () => setHistoryOpen(false),
    startNewConversation,
    selectProjectExecution,
    runSlash: (cmd: SlashCommandDef, argument?: string, options?: RunSlashOptions) => void runSlash(cmd, argument, options),
  });
  useEffect(() => registerBrainConversationStarter(actionHandlers.startNewConversation), [actionHandlers.startNewConversation]);
  const dockOpenerRef = useRef<(options: { sessionId: string; continuable: boolean; title?: string }) => Promise<void>>(async () => {});
  const dockOpenSequence = useRef(0);
  dockOpenerRef.current = async (options) => {
    const request = ++dockOpenSequence.current;
    if (options.continuable) {
      attachedRef.current = true;
      const confirmed = requireCurrentBinding(await selectSession({ session: options.sessionId }));
      if (confirmed.session !== options.sessionId) throw new Error('Conversation selection returned a different session.');
    } else {
      // The stream starts asynchronously; this authorized exact-session read refuses a missing target
      // before acknowledging the open, without creating a replacement session.
      const previousGeneration = genRef.current;
      const issuedIn = authGeneration();
      await elowenClient.brainMessagesPage(options.sessionId, { limit: 1 });
      if (request !== dockOpenSequence.current || previousGeneration !== genRef.current || issuedIn !== authGeneration()) throw new Error('Conversation selection was superseded.');
      attachedRef.current = true;
      const opening = openDelegatedChild({ sessionId: options.sessionId, conversation: options.sessionId, title: options.title ?? null, delegated: false }, false);
      const generation = genRef.current;
      await opening;
      if (generation !== genRef.current) throw new Error('Conversation selection was superseded.');
    }
  };
  useEffect(() => registerBrainConversationDockOpener((options) => dockOpenerRef.current(options)), []);
  const readOnlyListed = readOnlyView?.conversation ? sessions.data?.find((s) => s.id === readOnlyView.conversation) : undefined;
  const readOnlyTitle = readOnlyListed?.title || readOnlyView?.title || null;
  // A React Query result is a new object every render; narrow it to the fields consumers read so the
  // conversation list cannot invalidate the actions value for a reason nobody can see.
  const sessionsView = useMemo(
    () => ({ data: sessions.data, isLoading: sessions.isLoading, error: sessions.error, refetch: sessions.refetch }),
    [sessions.data, sessions.isLoading, sessions.error, sessions.refetch],
  );

  const idleProjectMove = useRef({ status: 'idle' as SaveStatus, retryTarget: null as ProjectExecutionRef | null }).current;
  const projectMove = projectMoves.get(activeSessionId ?? '') ?? idleProjectMove;
  const actions = useShallowStable<BrainChatActions>({
    ...actionHandlers,
    statsOpen, reasoningOpen, pluginPicker, pluginPickerCrashed: pluginPicker !== null && crashedPicker === pluginPicker, helpTab, modelOpen,
    activeSessionId, conversationIcon, conversationName, pluginSessionId: childFocus || readOnly ? null : activeSessionId, draft,
    reasoning, projectMove,
    models, currentModel, provider, providerLabel, usageProvider, modelsLoading, modelsError, modelStatus, modelQueued,
    showThoughts: thoughts === 'show',
    workMode, modeSwitching,
    renameOpen, historyOpen,
    commands,
    sessions: sessionsView,
  });
  const status = useShallowStable<BrainChatStatus>({
    busy, workPending, submittedRun, ready, reconnecting, hasSurface,
    connectionState, connectionFirstAttempt, connectionFailure, modelRetry, executionState: control?.executionState ?? null, programStatus: control?.programStatus ?? null,
    notice, ask, cards, processes, queued: viewingOtherSession ? NO_QUEUE : visibleQueue, membership, viewerId, guest, canStop, turnWriter, yolo, reissueTarget, conversationReadState, unreadBoundaryId, typing, replyTarget: activeSessionId && recovery.reply ? { sessionId: activeSessionId, messageId: recovery.reply.id } : null, recalledInputs: recovery.rows, readOnly, readOnlyChild, readOnlyTitle, childDelegation, childFocus, childFocusName,
    usage, telemetry, goal, promptSuggestion, subagents, workflows, lineCfg, fast, attachments, uploads,
    hasMoreHistory, focusNonce, sendNonce, planDecision, planSubmitting,
  });
  const transcript = useShallowStable<BrainChatTranscript>({ turns, messageWindow, thinking: view.thinking, narration, conversationAppendCount: view.conversationAppendCount });

  return { actions, status, transcript };
}

/** A send refused because an earlier one into the conversation is unconfirmed; that send's card already says so. */
class SendHeldError extends Error { constructor() { super('A send into this conversation is unconfirmed.'); } }

/** Mount ONCE (in ShellLayout, above the route content and the dock) so the single chat controller — SSE
 *  stream, transcript, draft, attachments, queue — outlives dock open/close, the Chat↔Terminál toggle and
 *  route changes. It is inert until the first chat open (ensureAttached), so a page that never opens chat
 *  never starts the brain. */
export function BrainChatProvider({ children }: { children: ReactNode }) {
  const { actions, status, transcript } = useBrainChatController();
  // Three values, three subscriptions: a consumer re-renders for the half it reads and for nothing else.
  // `children` is the same element on every render of this provider, so the subtree below is reconciled
  // only through these contexts — which is what makes the split worth anything at all.
  // Recovery stays in the chat footer beside independent model and execution state. The provider must
  // not cover navigation or the transcript when the transport is unavailable.
  return (
    <BrainChatActionsContext.Provider value={actions}>
      <BrainChatStatusContext.Provider value={status}>
        <BrainChatTranscriptContext.Provider value={transcript}>
          {children}
          {actions.pluginPicker ? (
            <PluginChatPickerHost
              invocation={actions.pluginPicker}
              sendAction={actions.sendPluginPicker}
              closeAction={actions.closePluginPicker}
              onCrash={actions.crashPluginPicker}
            />
          ) : null}
        </BrainChatTranscriptContext.Provider>
      </BrainChatStatusContext.Provider>
    </BrainChatActionsContext.Provider>
  );
}
